// El catálogo en español. **Es la fuente de verdad**: define qué claves existen,
// y TypeScript obliga a que el inglés tenga exactamente las mismas.
//
// ## Cómo se nombra una clave
//
// `área.pantalla.cosa`, de lo general a lo particular. Sirve para dos cosas:
// agrupa lo que se traduce junto, y al leer una clave suelta en el código se
// sabe de dónde salió sin ir a buscarla.
//
// ## Cuándo un texto lleva plural
//
// Cuando el número cambia la frase. Se escribe como objeto con las formas que
// define `Intl.PluralRules`, no con un `if` en el componente:
//
//   'ordenes.cantidad': { one: '{n} orden', other: '{n} órdenes' }
//
// Español e inglés usan `one` y `other`. Otros idiomas tienen más formas —el
// ruso tiene cuatro— y por eso se usan los nombres de Intl y no «singular» y
// «plural»: el día que entre un idioma así, la estructura ya sirve.
//
// ## Trato: tuteo
//
// «Revisa», «vuelve», «tienes». Nunca voseo («revisá», «volvé», «tenés»): la
// misma pantalla no puede hablarle a la persona de dos formas. Decidido en #78 y
// escrito en `CLAUDE.md`.
//
// ## Lo que NO va acá
//
// Nada que venga del servidor ni del usuario: nombres de talleres, notas de una
// orden, datos. Solo texto de la interfaz.

export const es = {
  // --- Lo común a todas las apps ---
  'app.marca': 'TallerYa',
  'app.cargando': 'Cargando…',
  'app.reintentar': 'Reintentar',
  'app.guardar': 'Guardar',
  'app.cancelar': 'Cancelar',
  'app.idioma': 'Idioma',

  // --- El patrón de listado de @tallerya/ui (F-7) ---
  //
  // Los textos que salen del Figma están tal cual allá, con tuteo. El resto del
  // catálogo usa voseo; cuando se decida uno, se alinea el otro.
  // Cuántas se ven, no cuántas hay: la API pagina por cursor y no devuelve el
  // total, así que «de 48» no se puede prometer. Ver `Paginacion.tsx`.
  'ui.listado.mostrando': {
    one: 'Mostrando 1 fila',
    other: 'Mostrando {n} filas',
  },
  'ui.listado.paginacion': 'Paginación',
  'ui.listado.paginaAnterior': 'Página anterior',
  'ui.listado.paginaSiguiente': 'Página siguiente',
  'ui.listado.acciones': 'Acciones',
  'ui.listado.accionesFila': 'Acciones de la fila',
  'ui.listado.limpiarFiltros': 'Limpiar filtros',
  'ui.listado.sinResultados.titulo': 'No encontramos resultados',
  'ui.listado.sinResultados.texto': 'Revisa la ortografía o prueba con menos palabras.',
  // El estado de error no está en el Figma: se armó con el mismo molde que «Sin
  // resultados». Dice qué hacer, no solo qué pasó.
  'ui.listado.error.titulo': 'No se pudo cargar la lista',
  'ui.listado.error.texto':
    'Puede ser la conexión o el servidor. Intenta de nuevo; si sigue igual, avisa a soporte.',

  // --- El ⓘ de un título o un campo (#120) ---
  'ui.ayuda.abrir': 'Más información sobre {tema}',

  // --- Un campo con `maximo` (#176): el `validation.too_long` dice cuánto cabe, y
  // el lector de pantalla avisa al acercarse y al pasarse, no a cada tecla ---
  'ui.campo.maximo': 'Máximo {maximo} caracteres',
  'ui.campo.cerca': 'Te acercas al máximo de {maximo} caracteres',
  'ui.campo.excedido': 'Pasaste el máximo de {maximo} caracteres',

  // --- El patrón de formulario de @tallerya/ui (F-8) ---
  //
  // Mismo criterio que el listado: tal cual el Figma, con tuteo.
  'ui.formulario.guardando': 'Guardando…',
  'ui.formulario.guardarYOtro': 'Guardar y agregar otro',
  'ui.formulario.aviso.titulo': 'No se pudo guardar',
  'ui.formulario.aviso.campos': {
    one: 'Revisa el campo marcado en rojo.',
    other: 'Revisa los {n} campos marcados en rojo.',
  },
  // Cuando no hubo una respuesta que entender: sin red, o el servidor caído. Lo
  // importante es la segunda mitad: lo escrito no se perdió.
  'ui.formulario.aviso.sinRespuesta':
    'No hubo respuesta del servidor. Revisa tu conexión e intenta guardar de nuevo: lo que escribiste sigue aquí.',
  // Un error de la API sobre un dato que esta pantalla no muestra (#76): no hay
  // campo que marcar en rojo, así que se dice qué pasó y a quién avisarle.
  'ui.formulario.aviso.fueraDePantalla':
    'No se pudo guardar por un dato que no aparece en esta pantalla: {detalle}. Si vuelve a pasar, avisa a soporte.',
  // El botón del aviso de cambio simultáneo (#178): trae la versión que guardó
  // la otra persona.
  'ui.formulario.recargar': 'Recargar',
  'ui.avisos.cerrar': 'Cerrar aviso',
  'ui.panel.cerrar': 'Cerrar',

  // --- Menú lateral del sitio administrativo (#98) ---
  //
  // Tal cual el «Menú lateral» del Figma.
  'admin.nav.inicio': 'Dashboard',
  'admin.nav.catalogo': 'Productos/Servicios',
  'admin.nav.clientes': 'Clientes',
  'admin.nav.activos': 'Activos',
  'admin.nav.personal': 'Mecánicos/Operadores',
  'admin.nav.ordenes': 'Órdenes de servicio',
  'admin.nav.gastos': 'Gastos',
  'admin.nav.agendas': 'Agendas',
  'admin.nav.reportes': 'Reportes',
  'admin.nav.configuracion': 'Configuraciones',
  'admin.nav.sucursales': 'Sucursales',
  'admin.nav.marcas': 'Marcas y modelos',
  'admin.nav.catalogos': 'Catálogos',
  'admin.nav.estadosDeOrden': 'Estados de la orden',
  'admin.nav.proveedores': 'Proveedores',
  'admin.nav.cerrarSesion': 'Cerrar sesión',
  // Lo que oye el lector de pantalla en un destino que todavía no tiene pantalla.
  'admin.nav.pronto': 'Próximamente',

  // --- Armazón: app bar y barra derecha (#99, #100) ---
  //
  // Los dos botones dicen qué abren y no «abrir» o «cerrar»: el estado ya lo
  // anuncia `aria-expanded`, y un texto que cambia con él lo diría dos veces.
  'admin.layout.menu': 'Menú de navegación',
  'admin.layout.panel': 'Barra lateral',
  'admin.layout.soporte': 'Soporte',
  'admin.layout.conectado': 'Con conexión',
  'admin.layout.sinConexion': 'Sin conexión',
  'admin.layout.tema': 'Tema claro',

  // --- Selector de sucursal del app bar (#101) ---
  //
  // La etiqueta del botón dice cuál está elegida: en celular solo se ve el
  // ícono, y el lector de pantalla necesita el nombre.
  'admin.sucursal.grupo': 'Sucursal',
  'admin.sucursal.actual': 'Sucursal: {nombre}',
  'admin.sucursal.principal': 'Principal',

  // --- Selector de taller (#205) ---
  'admin.taller.grupo': 'Taller',
  'admin.taller.actual': 'Taller: {nombre}',
  'admin.taller.error.titulo': 'No se pudieron leer tus talleres',
  'admin.taller.error.texto': 'Vuelve a intentar. Si sigue pasando, avisa a quien administra tu taller.',

  // --- Pantalla de inicio ---
  'admin.inicio.titulo': 'Administración del taller',
  'admin.inicio.entrada':
    'Desde aquí el dueño del taller da de alta sus sucursales, su gente, sus clientes y su catálogo de servicios y repuestos.',
  // Mientras el Dashboard no tenga su resumen del día (Figma `12:373`), el
  // aviso dice qué va a haber y dónde está lo demás. Sin atajos a las
  // pantallas: serían una segunda copia del menú, y el menú ya esconde lo que
  // el rol no puede abrir (#166).
  'admin.inicio.aviso.titulo': 'El resumen de hoy está en camino',
  'admin.inicio.aviso.texto':
    'Aquí vas a ver las órdenes abiertas, las listas para entregar y las citas del día. Mientras tanto, todo lo demás está en el menú.',

  // --- Sesión (F-5) ---
  'admin.sesion.entrar': 'Entrar',
  'admin.sesion.salir': 'Salir',
  'admin.sesion.error': 'No se pudo entrar.',
  'admin.sesion.cuenta': 'Tu cuenta: {nombre}',
  // Quien entró sin pertenecer a un taller, como el equipo de TallerYa (#168).
  'admin.acceso.sinTaller.titulo': 'Este usuario no pertenece a ningún taller',
  'admin.acceso.sinTaller.texto':
    'El administrativo es de cada taller, y esta cuenta no pertenece a ninguno. Cierra sesión y entra con el correo con el que te dio de alta tu taller.',
  'admin.acceso.sinTaller.cuenta': 'Entraste como {nombre}.',

  // --- Guardas de ruta (F-6) ---
  //
  // Los tres dicen qué hacer, no solo qué pasó. Quien ve una de estas pantallas
  // está atascado, y «no tienes permiso» a secas lo deja igual de atascado.
  'admin.guardia.sinSesion.titulo': 'Necesitas entrar',
  'admin.guardia.sinSesion.texto': 'Esta pantalla es del taller, así que hay que saber quién eres.',
  'admin.guardia.sinPermiso.titulo': 'No tienes permiso para ver esto',
  'admin.guardia.sinPermiso.texto':
    'Tu rol en el taller no incluye esta pantalla. Si la necesitas, pídeselo a quien administra el taller.',
  'admin.guardia.error.titulo': 'No se pudieron leer tus permisos',
  'admin.guardia.error.texto':
    'Puede que te hayan dado de baja de este taller, o que la API no esté respondiendo. Vuelve a entrar; si sigue igual, avisa a soporte.',

  // --- Estado de la conexión con la API (F-4) ---
  'admin.api.titulo': 'Conexión con la API',
  'admin.api.consultando': 'Preguntándole a la API…',
  'admin.api.sinSesion':
    'La API responde y pide credenciales. Entra para ver los datos de tu taller.',
  'admin.api.conSesion': 'Sesión activa en el taller {taller}.',
  'admin.api.sinApi': 'No se pudo contactar la API en {direccion}. ¿Está levantada?',

  // --- Sucursales (M-1) ---
  //
  // Tal cual los frames «Sucursales - …» del Figma. Es la maestra de muestra: las
  // demás siguen el mismo orden de claves —listado, menú, formulario, ventanas y
  // avisos—.
  'admin.sucursales.titulo': 'Sucursales',
  'admin.sucursales.info': 'Los locales del taller. Cada orden se abre en una sucursal, y el personal se asigna por sucursal.',
  'admin.sucursales.buscar': 'Buscar por nombre o código',
  'admin.sucursales.nueva': 'Nueva sucursal',
  'admin.sucursales.filtro.estado': 'Estado',
  'admin.sucursales.filtro.todas': 'Todas',
  'admin.sucursales.columna.codigo': 'Código',
  'admin.sucursales.columna.nombre': 'Nombre',
  'admin.sucursales.columna.direccion': 'Dirección',
  'admin.sucursales.columna.telefono': 'Teléfono',
  'admin.sucursales.columna.personal': 'Personal',
  'admin.sucursales.columna.estado': 'Estado',
  'admin.sucursales.principal': 'Principal',
  'admin.sucursales.enServicio': 'En servicio',
  'admin.sucursales.fueraDeServicio': 'Fuera de servicio',
  'admin.sucursales.vacio.titulo': 'Aún no hay sucursales',
  'admin.sucursales.vacio.texto':
    'Una sucursal es un local del taller: cada orden de servicio se abre en una y el personal se asigna por sucursal. La primera que crees será la principal.',
  'admin.sucursales.sinResultados': 'Ninguna sucursal coincide con «{texto}».',

  'admin.sucursales.menu.editar': 'Editar',
  'admin.sucursales.menu.asignarPersonal': 'Asignar personal',
  'admin.sucursales.menu.nombrarPrincipal': 'Nombrar principal',
  'admin.sucursales.menu.fueraDeServicio': 'Poner fuera de servicio',
  'admin.sucursales.menu.enServicio': 'Poner en servicio',
  'admin.sucursales.menu.eliminar': 'Eliminar',

  'admin.sucursales.nueva.descripcion':
    'La primera sucursal del taller queda como principal. Los campos marcados con * son obligatorios.',
  'admin.sucursales.editar.titulo': 'Editar sucursal',
  'admin.sucursales.editar.info': 'Corrige los datos de la sucursal. Los campos marcados con * son obligatorios.',
  'admin.sucursales.formulario.seccion': 'Información general',
  'admin.sucursales.formulario.seccionTexto':
    'Cómo se reconoce la sucursal en las órdenes y en los reportes.',
  'admin.sucursales.formulario.codigo': 'Código',
  'admin.sucursales.formulario.codigoAyuda': 'Si lo dejas vacío, se usa el siguiente correlativo.',
  'admin.sucursales.formulario.nombre': 'Nombre',
  'admin.sucursales.formulario.nombreEjemplo': 'Ej. Centro',
  'admin.sucursales.formulario.telefono': 'Teléfono',
  'admin.sucursales.formulario.direccion': 'Dirección',
  'admin.sucursales.formulario.direccionEjemplo': 'Calle, número, colonia y ciudad',
  'admin.sucursales.formulario.guardarCambios': 'Guardar cambios',
  'admin.sucursales.formulario.eliminar': 'Eliminar sucursal',
  'admin.sucursales.noExiste.titulo': 'Esta sucursal no existe',
  'admin.sucursales.noExiste.texto':
    'Puede que la hayan eliminado. Vuelve al listado para ver las sucursales del taller.',
  'admin.sucursales.volver': 'Volver a sucursales',

  'admin.sucursales.asignar.titulo': 'Personal de {nombre}',
  'admin.sucursales.asignar.texto':
    'Marca quién trabaja en esta sucursal. Una persona puede estar en varias.',
  'admin.sucursales.asignar.seleccionadas': { one: '1 seleccionada', other: '{n} seleccionadas' },
  'admin.sucursales.asignar.sinPersonal':
    'Aún no hay personal activo. Da de alta a tus mecánicos en Personal y vuelve aquí para asignarlos.',
  'admin.sucursales.asignar.error': 'No se pudo leer el personal. Cierra la ventana e intenta de nuevo.',

  'admin.sucursales.eliminar.titulo': 'Eliminar sucursal',
  'admin.sucursales.eliminar.texto':
    'Vas a eliminar {nombre} ({codigo}). Dejará de aparecer al abrir órdenes y al asignar personal; sus órdenes anteriores se conservan. Su código y su nombre quedan libres.',
  'admin.sucursales.eliminar.boton': 'Eliminar',

  'admin.sucursales.aviso.creada': 'Se creó la sucursal {nombre}',
  'admin.sucursales.aviso.guardada': 'Se guardaron los cambios de {nombre}',
  'admin.sucursales.aviso.principal': '{nombre} es ahora la sucursal principal',
  'admin.sucursales.aviso.fueraDeServicio': '{nombre} quedó fuera de servicio',
  'admin.sucursales.aviso.enServicio': '{nombre} volvió a estar en servicio',
  'admin.sucursales.aviso.eliminada': 'Se eliminó la sucursal {nombre}',
  'admin.sucursales.aviso.personal': 'Se guardó el personal de {nombre}',
  'admin.sucursales.error.titulo': 'No se pudo completar',

  // --- Proveedores (M-2, #56) ---
  'admin.proveedores.titulo': 'Proveedores',
  'admin.proveedores.info':
    'A quién le compra el taller repuestos e insumos. Se eligen al registrar un gasto; un proveedor inactivo deja de ofrecerse.',
  'admin.proveedores.buscar': 'Buscar por nombre, código o documento',
  'admin.proveedores.nuevo': 'Nuevo proveedor',
  'admin.proveedores.filtro.estado': 'Estado',
  'admin.proveedores.filtro.todos': 'Todos',
  'admin.proveedores.filtro.activos': 'Activos',
  'admin.proveedores.filtro.inactivos': 'Inactivos',
  'admin.proveedores.columna.codigo': 'Código',
  'admin.proveedores.columna.nombre': 'Nombre',
  'admin.proveedores.columna.documento': 'Documento',
  'admin.proveedores.columna.referencia': 'Referencia',
  'admin.proveedores.columna.estado': 'Estado',
  'admin.proveedores.activo': 'Activo',
  'admin.proveedores.inactivo': 'Inactivo',
  'admin.proveedores.vacio.titulo': 'Aún no hay proveedores',
  'admin.proveedores.vacio.texto':
    'Un proveedor es a quien el taller le compra repuestos e insumos. Lo eliges al registrar un gasto, y si todavía no existe, lo creas ahí mismo.',
  'admin.proveedores.sinResultados': 'Ningún proveedor coincide con «{texto}».',

  'admin.proveedores.menu.verFicha': 'Ver ficha',
  'admin.proveedores.menu.editar': 'Editar',
  'admin.proveedores.menu.desactivar': 'Desactivar',
  'admin.proveedores.menu.activar': 'Activar',
  'admin.proveedores.menu.eliminar': 'Eliminar',

  'admin.proveedores.nuevo.info':
    'Basta con el nombre: el resto lo puedes completar después. Los campos marcados con * son obligatorios.',
  'admin.proveedores.editar.titulo': 'Editar proveedor',
  'admin.proveedores.editar.info': 'Corrige los datos del proveedor. Los campos marcados con * son obligatorios.',
  'admin.proveedores.formulario.seccion': 'Información general',
  'admin.proveedores.formulario.seccionInfo': 'Cómo se reconoce al proveedor al registrar un gasto.',
  'admin.proveedores.formulario.codigo': 'Código',
  'admin.proveedores.formulario.codigoAyuda': 'Se asigna solo. Puedes reemplazarlo.',
  'admin.proveedores.formulario.nombre': 'Nombre',
  'admin.proveedores.formulario.nombreEjemplo': 'Ej. Repuestos del Sur',
  'admin.proveedores.formulario.tipoDocumento': 'Tipo de documento',
  'admin.proveedores.formulario.sinDocumento': 'Sin documento',
  'admin.proveedores.formulario.numeroDocumento': 'Número de documento',
  'admin.proveedores.formulario.referencia': 'Referencia',
  'admin.proveedores.formulario.referenciaAyuda':
    'El número de cuenta o de cliente que te dio el proveedor. Es lo que dictas al hacer un pedido.',
  'admin.proveedores.formulario.referenciaEjemplo': 'Ej. C-88412',
  'admin.proveedores.formulario.guardarCambios': 'Guardar cambios',
  'admin.proveedores.formulario.eliminar': 'Eliminar proveedor',
  'admin.proveedores.noExiste.titulo': 'Este proveedor no existe',
  'admin.proveedores.noExiste.texto':
    'Puede que lo hayan eliminado. Vuelve al listado para ver los proveedores del taller.',
  'admin.proveedores.volver': 'Volver a proveedores',
  // La ficha (#188): llegó con el historial.
  'admin.proveedores.ficha.info':
    'Los datos del proveedor y quién los cambió. Para corregir algo, usa «Editar proveedor».',
  'admin.proveedores.ficha.desde': 'Proveedor desde el {fecha}',
  'admin.proveedores.mas': 'Más acciones',
  'admin.proveedores.historial.info':
    'Quién registró el proveedor y quién le cambió qué, lo más nuevo arriba. Del código, el nombre y el número de documento se ve que cambiaron, pero no a qué: un proveedor puede ser una persona, y no se guardan.',

  'admin.proveedores.eliminar.titulo': 'Eliminar proveedor',
  'admin.proveedores.eliminar.texto':
    'Vas a eliminar {nombre} ({codigo}). Dejará de aparecer al registrar gastos; los gastos que ya lo usan se conservan. Su código, su nombre y su documento quedan libres.',
  'admin.proveedores.eliminar.boton': 'Eliminar proveedor',

  'admin.proveedores.vuelo.titulo': 'Nuevo proveedor',
  'admin.proveedores.vuelo.info':
    'Se crea con el nombre y el código se asigna solo. Los demás datos los completas después en Proveedores.',
  'admin.proveedores.vuelo.crear': 'Crear proveedor',

  'admin.proveedores.aviso.creado': 'Se creó el proveedor {nombre}',
  'admin.proveedores.aviso.guardado': 'Se guardaron los cambios de {nombre}',
  'admin.proveedores.aviso.activado': '{nombre} volvió a estar activo',
  'admin.proveedores.aviso.desactivado': '{nombre} quedó inactivo',
  'admin.proveedores.aviso.eliminado': 'Se eliminó el proveedor {nombre}',
  'admin.proveedores.error.titulo': 'No se pudo completar',
  // Lo que se suma al aviso del alta y la edición cuando el nombre o el documento ya los tiene otro proveedor.
  'admin.proveedores.error.repetidoNombre': '{nombre} ({codigo}) ya está registrado con ese nombre.',
  'admin.proveedores.error.repetidoDocumento': '{nombre} ({codigo}) ya está registrado con ese documento.',
  'admin.proveedores.error.repetidoAmbos': '{nombre} ({codigo}) ya está registrado con ese nombre y ese documento.',
  'admin.proveedores.error.verExistente': 'Ver proveedor existente →',

  // --- Personal (M-4, #58) ---
  //
  // Lo del acceso al sistema —rol, PIN, QR, dispositivos— no está aquí: es de #208.
  'admin.personal.titulo': 'Mecánicos/Operadores',
  'admin.personal.info':
    'La gente del taller: mecánicos, recepción y administración. A quien está activo se le puede asignar trabajo en las órdenes.',
  'admin.personal.buscar': 'Buscar por nombre o código',
  'admin.personal.nuevo': 'Nuevo mecánico u operador',
  'admin.personal.filtro.especialidad': 'Especialidad',
  'admin.personal.filtro.sucursal': 'Sucursal',
  'admin.personal.filtro.estado': 'Estado',
  'admin.personal.filtro.todas': 'Todas',
  'admin.personal.filtro.todos': 'Todos',
  'admin.personal.filtro.activos': 'Activos',
  'admin.personal.filtro.inactivos': 'Inactivos',
  'admin.personal.columna.codigo': 'Código',
  'admin.personal.columna.nombre': 'Nombre',
  'admin.personal.columna.especialidades': 'Especialidades',
  'admin.personal.columna.sucursales': 'Sucursal',
  'admin.personal.columna.telefono': 'Teléfono',
  'admin.personal.columna.estado': 'Estado',
  'admin.personal.activo': 'Activo',
  'admin.personal.inactivo': 'Inactivo',
  'admin.personal.vacio.titulo': 'Aún no hay personal registrado',
  'admin.personal.vacio.texto':
    'Da de alta a tus mecánicos y operadores con sus especialidades: así sabes a quién asignarle cada trabajo.',
  'admin.personal.sinResultados': 'Nadie coincide con «{texto}».',

  'admin.personal.menu.verFicha': 'Ver ficha',
  'admin.personal.menu.editar': 'Editar',
  'admin.personal.menu.cambiarCodigo': 'Cambiar código',
  'admin.personal.menu.desactivar': 'Desactivar',
  'admin.personal.menu.activar': 'Activar',
  'admin.personal.menu.eliminar': 'Eliminar',

  'admin.personal.nuevo.info':
    'Basta con el nombre y el apellido: el resto lo puedes completar después. Los campos marcados con * son obligatorios.',
  'admin.personal.editar.titulo': 'Editar mecánico u operador',
  'admin.personal.editar.info':
    'Corrige los datos de la persona. El código se cambia desde su ficha, porque es su usuario para entrar. Los campos marcados con * son obligatorios.',
  'admin.personal.formulario.datos': 'Datos personales',
  'admin.personal.formulario.datosInfo': 'Cómo se reconoce a la persona en las órdenes y en el taller.',
  'admin.personal.formulario.nombre': 'Nombre',
  'admin.personal.formulario.nombreEjemplo': 'Ej. Carlos',
  'admin.personal.formulario.apellido': 'Apellido',
  'admin.personal.formulario.apellidoEjemplo': 'Ej. Méndez',
  'admin.personal.formulario.codigo': 'Código',
  'admin.personal.formulario.codigoAyuda':
    'Se asigna solo y puedes reemplazarlo. Es su usuario para entrar al sistema.',
  'admin.personal.formulario.ingreso': 'Fecha de ingreso',
  'admin.personal.formulario.telefono': 'Teléfono',
  'admin.personal.formulario.correo': 'Correo',
  'admin.personal.formulario.correoAyuda': 'Opcional: muchos mecánicos no tienen. Para entrar le basta su código.',
  'admin.personal.formulario.trabajo': 'Trabajo',
  'admin.personal.formulario.trabajoInfo': 'Qué sabe hacer y dónde trabaja: define a quién se le asigna cada trabajo.',
  'admin.personal.formulario.especialidades': 'Especialidades',
  'admin.personal.formulario.especialidadesAyuda':
    'Escribe una y presiona Enter. Si no existe, se crea al guardar: «eléctrico», «enderezado»…',
  'admin.personal.formulario.especialidadesPlaceholder': 'Escribe y presiona Enter',
  'admin.personal.formulario.quitar': 'Quitar',
  'admin.personal.formulario.sucursales': 'Sucursales',
  'admin.personal.formulario.sucursalesAyuda':
    'Dónde trabaja; puede ser más de una. Sin ninguna marcada, no queda limitada a una sucursal.',
  'admin.personal.formulario.guardarCambios': 'Guardar cambios',
  'admin.personal.formulario.eliminar': 'Eliminar',
  'admin.personal.noExiste.titulo': 'Esta persona no existe',
  'admin.personal.noExiste.texto':
    'Puede que la hayan eliminado. Vuelve al listado para ver al personal del taller.',
  'admin.personal.volver': 'Volver a Mecánicos/Operadores',

  'admin.personal.ficha.info':
    'Los datos de la persona, sus especialidades y dónde trabaja. Para corregir algo, usa «Editar».',
  'admin.personal.ficha.desde': 'Registrado el {fecha}',
  'admin.personal.ficha.datos': 'Datos',
  'admin.personal.ficha.datosInfo': 'Cómo contactarla, desde cuándo trabaja en el taller y qué sabe hacer.',
  'admin.personal.ficha.sinCorreo': '— (no tiene)',
  'admin.personal.ficha.sinSucursales': 'Todas',
  'admin.personal.ficha.sinEspecialidades': 'Sin especialidades',
  'admin.personal.mas': 'Más acciones',

  'admin.personal.codigo.titulo': 'Cambiar código',
  'admin.personal.codigo.info':
    'El código es el usuario con el que la persona entra al sistema. Al cambiarlo, su usuario se renombra y desde ahí entra con el código nuevo. Si el sistema de identidad no responde, el cambio se completa solo cuando vuelva.',
  'admin.personal.codigo.actual': 'Código actual: {codigo}',
  'admin.personal.codigo.nuevo': 'Código nuevo',
  'admin.personal.codigo.boton': 'Cambiar código',

  'admin.personal.eliminar.titulo': 'Eliminar',
  'admin.personal.eliminar.texto':
    'Vas a eliminar a {nombre} ({codigo}). Eliminar es solo para alguien registrado por error. Su código no queda libre, porque su usuario sigue existiendo.',
  'admin.personal.eliminar.boton': 'Eliminar',
  // Con trabajo registrado no se elimina (#163): se desactiva, como un activo con órdenes.
  'admin.personal.eliminar.bloqueado.titulo': 'No se puede eliminar',
  'admin.personal.eliminar.bloqueado.activo':
    '{nombre} {historial}. Eliminar es solo para alguien registrado por error. Si ya no trabaja en el taller, desactívalo: su historial se conserva y deja de ofrecerse para asignar trabajo.',
  'admin.personal.eliminar.bloqueado.inactivo':
    '{nombre} {historial}. Eliminar es solo para alguien registrado por error. Ya está desactivado: su historial se conserva y no se le asigna trabajo.',
  'admin.personal.eliminar.bloqueado.ordenes': { one: 'aparece en 1 orden', other: 'aparece en {n} órdenes' },
  'admin.personal.eliminar.bloqueado.cambios': 'ya registró cambios en el sistema',
  'admin.personal.eliminar.entendido': 'Entendido',
  'admin.personal.eliminar.desactivar': 'Desactivar',

  'admin.personal.aviso.creado': 'Se registró a {nombre} con el código {codigo}',
  'admin.personal.aviso.guardado': 'Se guardaron los cambios de {nombre}',
  'admin.personal.aviso.codigo': 'El código de {nombre} ahora es {codigo}',
  'admin.personal.aviso.activado': '{nombre} volvió a estar activo',
  'admin.personal.aviso.desactivado': '{nombre} quedó inactivo',
  'admin.personal.aviso.eliminado': 'Se eliminó a {nombre}',
  'admin.personal.error.titulo': 'No se pudo completar',

  // --- Productos/Servicios (M-3, #112) ---
  'admin.catalogo.titulo': 'Productos/Servicios',
  'admin.catalogo.info': 'Los servicios y productos que ofrece el taller, con su precio de lista. Son los que se agregan a las órdenes; lo inactivo deja de ofrecerse.',
  'admin.catalogo.nuevo': 'Nuevo producto o servicio',
  'admin.catalogo.buscar': 'Buscar por código o por nombre',
  'admin.catalogo.filtro.tipo': 'Tipo',
  'admin.catalogo.filtro.categoria': 'Categoría',
  'admin.catalogo.filtro.estado': 'Estado',
  'admin.catalogo.filtro.todos': 'Todos',
  'admin.catalogo.filtro.todas': 'Todas',
  'admin.catalogo.columna.codigo': 'Código',
  'admin.catalogo.columna.nombre': 'Nombre',
  'admin.catalogo.columna.tipo': 'Tipo',
  'admin.catalogo.columna.categoria': 'Categoría',
  'admin.catalogo.columna.precio': 'Precio',
  'admin.catalogo.columna.duracion': 'Duración',
  'admin.catalogo.columna.estado': 'Estado',
  'admin.catalogo.servicio': 'Servicio',
  'admin.catalogo.producto': 'Producto',
  'admin.catalogo.servicios': 'Servicios',
  'admin.catalogo.productos': 'Productos',
  'admin.catalogo.activo': 'Activo',
  'admin.catalogo.inactivo': 'Inactivo',
  'admin.catalogo.vacio.titulo': 'Aún no hay productos ni servicios',
  'admin.catalogo.vacio.texto':
    'Carga lo que el taller ofrece para poder agregarlo a las órdenes: cambios de aceite, frenos, diagnósticos, repuestos.',
  'admin.catalogo.sinResultados': 'Ningún ítem coincide con «{texto}».',
  // Con el buscador y algún combo puestos (#169), como en Clientes: «… y el filtro Estado: Inactivo.»
  'admin.catalogo.sinResultados.conFiltros': {
    one: 'Ningún ítem coincide con «{texto}» y el filtro {filtros}.',
    other: 'Ningún ítem coincide con «{texto}» y los filtros {filtros}.',
  },
  'admin.catalogo.sinResultados.buscarYCombos': 'Revisa la ortografía o quita los filtros para ver más resultados.',
  'admin.catalogo.sinResultados.combos':
    'Ningún ítem cumple con el tipo, la categoría o el estado elegidos. Cámbialos o limpia los filtros.',
  'admin.catalogo.sinResultados.pagina': 'No quedan ítems en esta página. Vuelve al inicio de la lista.',
  'admin.catalogo.menu.verFicha': 'Ver ficha',
  'admin.catalogo.menu.editar': 'Editar',
  'admin.catalogo.menu.duplicar': 'Duplicar',
  'admin.catalogo.menu.activar': 'Activar',
  'admin.catalogo.menu.desactivar': 'Desactivar',
  'admin.catalogo.menu.eliminar': 'Eliminar',
  'admin.catalogo.mas': 'Más acciones',
  'admin.catalogo.menu.importarArchivo': 'Importar desde archivo',
  'admin.catalogo.exportar.grupo': 'Exportar',
  'admin.catalogo.exportar.aExcel': 'Exportar a Excel',
  'admin.catalogo.exportar.aCsv': 'Exportar a CSV',
  'admin.catalogo.exportar.aPdf': 'Exportar a PDF',
  'admin.catalogo.exportar.nota': 'Se exporta lo que muestra el filtro actual.',
  'admin.catalogo.exportar.excel': 'Excel',
  'admin.catalogo.exportar.csv': 'CSV',
  'admin.catalogo.importar.titulo': 'Importar productos y servicios',
  'admin.catalogo.importar.descripcion': 'Sube un Excel o un CSV con un producto o servicio por fila.',
  'admin.catalogo.importar.columnas': 'Columnas: Código, Tipo, Nombre, Categoría, Precio y Duración (min). Solo Tipo y Nombre son obligatorias; sin código, se asigna uno. Las categorías que no existan se crean.',
  'admin.catalogo.importar.plantilla': 'Descargar plantilla:',
  'admin.catalogo.importar.plantillaError': 'No se pudo bajar la plantilla. Vuelve a intentar.',
  'admin.catalogo.importar.zona': 'Arrastra el archivo aquí o',
  'admin.catalogo.importar.elegir': 'elígelo',
  'admin.catalogo.importar.formatos': 'Excel (.xlsx) o CSV, hasta 5 MB',
  'admin.catalogo.importar.revisando': 'Revisando {archivo}…',
  'admin.catalogo.importar.tipoInvalido': 'El archivo tiene que ser Excel (.xlsx) o CSV.',
  'admin.catalogo.importar.muyGrande': 'El archivo pasa de 5 MB. Pártelo en varios y vuelve a intentar.',
  'admin.catalogo.importar.error.titulo': 'No se pudo revisar el archivo',
  'admin.catalogo.importar.error.importar': 'No se pudo importar',
  'admin.catalogo.importar.leidas': { one: 'Se leyó 1 fila de {archivo}.', other: 'Se leyeron {n} filas de {archivo}.' },
  'admin.catalogo.importar.listos': { one: '1 ítem listo', other: '{n} ítems listos' },
  'admin.catalogo.importar.conErrores': { one: '1 fila con errores', other: '{n} filas con errores' },
  'admin.catalogo.importar.nuevasCategorias': 'Se van a crear estas categorías: {lista}.',
  'admin.catalogo.importar.columna.fila': 'Fila',
  'admin.catalogo.importar.columna.nombre': 'Nombre',
  'admin.catalogo.importar.columna.problema': 'Problema',
  'admin.catalogo.importar.nada': 'Ninguna fila se puede importar. Corrige el archivo y vuelve a subirlo.',
  'admin.catalogo.importar.otro': 'Elegir otro archivo',
  'admin.catalogo.importar.cancelar': 'Cancelar',
  'admin.catalogo.importar.cerrar': 'Cerrar',
  'admin.catalogo.importar.boton': { one: 'Importar el válido', other: 'Importar los {n} válidos' },
  'admin.catalogo.importar.hecho': { one: 'Se importó 1 producto o servicio', other: 'Se importaron {n} productos y servicios' },
  'admin.catalogo.importar.cambiaron': {
    one: '1 fila cambió desde la revisión y quedó afuera.',
    other: '{n} filas cambiaron desde la revisión y quedaron afuera.',
  },
  'admin.catalogo.aviso.activado': '«{nombre}» está activo de nuevo',
  'admin.catalogo.aviso.desactivado': '«{nombre}» quedó inactivo',
  'admin.catalogo.aviso.eliminado': '«{nombre}» se eliminó del catálogo',
  'admin.catalogo.error.titulo': 'No se pudo completar la acción',
  'admin.catalogo.error.reintentar': 'Vuelve a intentar en un momento. Si sigue fallando, revisa tu conexión.',
  'admin.catalogo.editar.titulo': 'Editar servicio',
  'admin.catalogo.editar.tituloProducto': 'Editar producto',
  'admin.catalogo.editar.info': 'El precio nuevo vale para las órdenes nuevas: las que ya lo llevan conservan el precio que se cobró. Los campos marcados con * son obligatorios.',
  'admin.catalogo.formulario.entrada':
    'Registra lo que el taller ofrece. Los campos marcados con * son obligatorios.',
  'admin.catalogo.formulario.general': 'Información general',
  'admin.catalogo.formulario.tipo': 'Tipo',
  'admin.catalogo.formulario.codigo': 'Código',
  'admin.catalogo.formulario.codigoAyuda': 'Se asigna solo. Puedes reemplazarlo.',
  'admin.catalogo.formulario.codigoAvisoEdicion':
    'Si lo cambias, los documentos impresos con el código anterior ya no lo encontrarán.',
  'admin.catalogo.formulario.nombre': 'Nombre',
  'admin.catalogo.formulario.categoria': 'Categoría',
  'admin.catalogo.formulario.sinCategoria': 'Sin categoría',
  'admin.catalogo.formulario.agregarCategoria': 'Agregar categoría',
  'admin.catalogo.formulario.categoriaInactiva': '(Categoría desactivada)',
  'admin.catalogo.formulario.codigoExterno': 'Código externo',
  'admin.catalogo.formulario.codigoExternoAyuda': 'El del proveedor o el de la caja. Opcional.',
  'admin.catalogo.formulario.precioYTiempo': 'Precio y tiempo',
  'admin.catalogo.formulario.precioYTiempoAyuda':
    'Son valores de referencia: en cada orden se pueden ajustar.',
  'admin.catalogo.formulario.precio': 'Precio de lista',
  'admin.catalogo.formulario.precioAvisoEdicion':
    'Cambiar el precio no modifica las órdenes ya registradas.',
  'admin.catalogo.formulario.duracion': 'Duración estimada',
  'admin.catalogo.formulario.horas': 'horas',
  'admin.catalogo.formulario.voz': 'Reconocimiento por voz',
  'admin.catalogo.formulario.vozAyuda':
    'Cómo le dice la gente. Ayuda a que el dictado del mecánico encuentre este servicio.',
  'admin.catalogo.formulario.otrosNombres': 'Otros nombres',
  'admin.catalogo.formulario.otrosNombresPlaceholder': 'Escribe y presiona Enter',
  'admin.catalogo.formulario.quitar': 'Quitar',
  'admin.catalogo.formulario.estado': 'Estado',
  'admin.catalogo.formulario.activoAyuda':
    'Disponible para agregarse a órdenes. Apágalo cuando ya no se ofrezca.',
  'admin.catalogo.formulario.guardarCambios': 'Guardar cambios',
  'admin.catalogo.formulario.desactivar': 'Desactivar',
  'admin.catalogo.formulario.creado': 'Creado el {fecha}',
  'admin.catalogo.formulario.modificado': 'Última modificación: {fecha}',
  'admin.catalogo.formulario.modificadoPor': 'Última modificación: {fecha} por {autor}',
  'admin.catalogo.formulario.hoy': 'hoy',
  'admin.catalogo.noExiste.titulo': 'Este producto o servicio no existe',
  'admin.catalogo.noExiste.texto': 'Puede que se haya eliminado, o que el enlace sea de otro taller.',
  'admin.catalogo.volver': 'Volver a Productos/Servicios',
  'admin.catalogo.ficha.migas': 'Ruta de navegación',
  'admin.catalogo.ficha.info': 'Todo lo de este servicio o producto, sin abrirlo para editar. Desde aquí se edita, se duplica, se desactiva o se elimina.',
  'admin.catalogo.ficha.sinAlias': 'Sin otros nombres. Agrégalos al editar para que el dictado del mecánico lo encuentre.',
  'admin.catalogo.ficha.uso': 'Uso en órdenes',
  'admin.catalogo.historial.info':
    'Quién creó este servicio o producto y quién le cambió qué, de qué valor a cuál, lo más nuevo arriba.',
  'admin.catalogo.ficha.sinOrdenesTexto': 'Todavía se puede eliminar: no toca ningún historial.',
  'admin.catalogo.ficha.conOrdenesTexto': 'Esas órdenes conservan el precio que se cobró. Para dejar de ofrecerlo, desactívalo desde «Más acciones».',
  'admin.catalogo.ficha.conOrdenesInactivo': 'Esas órdenes conservan el precio que se cobró. Ya está inactivo: no se ofrece al crear órdenes nuevas.',
  'admin.catalogo.aviso.guardado': 'Se guardó «{nombre}»',
  'admin.catalogo.aviso.guardadoTexto': 'Ya se puede agregar a las órdenes.',
  'admin.catalogo.categoria.titulo': 'Nueva categoría',
  'admin.catalogo.categoria.descripcion': 'Se guarda en el catálogo del taller.',
  'admin.catalogo.categoria.nombre': 'Nombre de la categoría',
  'admin.catalogo.categoria.guardar': 'Guardar categoría',
  'admin.catalogo.categoria.cancelar': 'Cancelar',
  'admin.catalogo.desactivar.tituloServicio': '¿Desactivar este servicio?',
  'admin.catalogo.desactivar.tituloProducto': '¿Desactivar este producto?',
  'admin.catalogo.desactivar.texto':
    '«{nombre}» dejará de aparecer al crear órdenes nuevas y al dictar por voz.',
  'admin.catalogo.desactivar.ordenes': 'Está registrado en {n} órdenes.',
  'admin.catalogo.desactivar.unaOrden': 'Está registrado en 1 orden.',
  'admin.catalogo.desactivar.ordenesNoCambian':
    'Esas órdenes no cambian: conservan el servicio y el precio que se cobró. Tampoco se borra nada del historial.',
  'admin.catalogo.desactivar.reactivar':
    'Puedes volver a activarlo cuando quieras desde el listado, con el filtro Estado: Inactivos.',
  'admin.catalogo.desactivar.confirmar': 'Sí, desactivar',
  'admin.catalogo.desactivar.cancelar': 'Cancelar',
  'admin.catalogo.confirmar.cargando': 'Cargando…',
  'admin.catalogo.confirmar.error': 'No se pudo leer el producto o servicio. Vuelve a intentar.',
  'admin.catalogo.confirmar.cerrar': 'Cerrar',
  'admin.catalogo.eliminar.tituloServicio': '¿Eliminar este servicio?',
  'admin.catalogo.eliminar.tituloProducto': '¿Eliminar este producto?',
  'admin.catalogo.eliminar.texto': '«{nombre}» se quita del catálogo y su código queda libre para otro.',
  'admin.catalogo.eliminar.sinOrdenes': 'No aparece en ninguna orden.',
  'admin.catalogo.eliminar.sinOrdenesTexto': 'Por eso se puede eliminar sin tocar el historial.',
  'admin.catalogo.eliminar.irreversible': 'No se puede deshacer. Si solo quieres dejar de ofrecerlo, desactívalo.',
  'admin.catalogo.eliminar.confirmar': 'Sí, eliminar',
  'admin.catalogo.eliminar.cancelar': 'Cancelar',
  'admin.catalogo.eliminar.noSePuedeServicio': 'No se puede eliminar este servicio',
  'admin.catalogo.eliminar.noSePuedeProducto': 'No se puede eliminar este producto',
  'admin.catalogo.eliminar.noSePuedeTexto': '«{nombre}» ya se usó: solo se elimina lo que no aparece en ninguna orden.',
  'admin.catalogo.eliminar.desactivarEnSuLugar': 'Para que deje de ofrecerse, desactívalo. Esas órdenes no cambian: conservan lo que se cobró.',
  'admin.catalogo.eliminar.yaInactivo': 'Ya está inactivo: no se ofrece al crear órdenes nuevas.',
  'admin.catalogo.eliminar.desactivar': 'Desactivar',

  // --- Clientes (M-5) ---
  //
  // Del Figma «Clientes» (listado, agregar, editar, eliminar), con los ajustes
  // de #104: la búsqueda también por documento y el teléfono que se filtra
  // completo.
  'admin.clientes.titulo': 'Clientes',
  'admin.clientes.info': 'Las personas y empresas que atiende el taller. Toca uno para ver su ficha, con sus contactos y sus activos. El buscador encuentra por una parte del nombre o del código; el documento, el teléfono y el correo se escriben completos.',
  'admin.clientes.nuevo': 'Nuevo cliente',
  'admin.clientes.nuevo.info': 'Con la clase y el nombre ya se puede guardar; lo demás se completa después. Los campos marcados con * son obligatorios.',
  'admin.clientes.buscar': 'Buscar por nombre, teléfono o correo',
  'admin.clientes.filtro.tipo': 'Tipo',
  'admin.clientes.filtro.todos': 'Todos',
  'admin.clientes.filtro.estado': 'Estado',
  'admin.clientes.columna.codigo': 'Código',
  'admin.clientes.columna.nombre': 'Nombre',
  'admin.clientes.columna.tipo': 'Tipo',
  'admin.clientes.columna.documento': 'Documento',
  'admin.clientes.columna.telefono': 'Teléfono',
  'admin.clientes.columna.correo': 'Correo',
  'admin.clientes.columna.vehiculos': 'Vehículos',
  'admin.clientes.columna.estado': 'Estado',
  'admin.clientes.persona': 'Persona',
  'admin.clientes.empresa': 'Empresa',
  'admin.clientes.activo': 'Activo',
  'admin.clientes.inactivo': 'Inactivo',
  'admin.clientes.sinResultados': 'Ningún cliente coincide con «{texto}».',
  // Con el buscador y algún combo puestos (#160): «… y el filtro Tipo: Persona.»
  'admin.clientes.sinResultados.conFiltros': {
    one: 'Ningún cliente coincide con «{texto}» y el filtro {filtros}.',
    other: 'Ningún cliente coincide con «{texto}» y los filtros {filtros}.',
  },
  'admin.clientes.sinResultados.buscar': 'Revisa la ortografía. El documento, el teléfono y el correo se escriben completos.',
  'admin.clientes.sinResultados.buscarYCombos': 'Revisa la ortografía o quita los filtros para ver más resultados.',
  'admin.clientes.sinResultados.combos': 'Ningún cliente cumple con el tipo o el estado elegidos. Cámbialos o limpia los filtros.',
  'admin.clientes.sinResultados.pagina': 'No quedan clientes en esta página. Vuelve al inicio de la lista.',
  'admin.clientes.vacio.titulo': 'Aún no hay clientes',
  'admin.clientes.vacio.texto': 'Registra a las personas y empresas que atiende el taller para poder abrirles órdenes.',
  'admin.clientes.vacio.nota': '¿Son muchos? Importa un Excel con tipo, nombre, documento, teléfono y correo.',
  'admin.clientes.mas': 'Más acciones',
  'admin.clientes.menu.importarArchivo': 'Importar desde archivo',
  'admin.clientes.exportar.grupo': 'Exportar',
  'admin.clientes.exportar.aExcel': 'Exportar a Excel',
  'admin.clientes.exportar.aCsv': 'Exportar a CSV',
  'admin.clientes.exportar.aPdf': 'Exportar a PDF',
  'admin.clientes.exportar.nota': 'Se exporta lo que muestra el filtro actual.',
  'admin.clientes.menu.editar': 'Editar',
  'admin.clientes.menu.activar': 'Activar',
  'admin.clientes.menu.desactivar': 'Desactivar',
  'admin.clientes.menu.eliminar': 'Eliminar',
  'admin.clientes.editar.titulo': 'Editar cliente',
  'admin.clientes.editar.info': 'Corrige los datos del cliente. Los campos marcados con * son obligatorios.',
  'admin.clientes.noExiste.titulo': 'Este cliente no existe',
  'admin.clientes.noExiste.texto':
    'Puede que lo hayan eliminado, o que el enlace esté mal. Vuelve al listado para buscarlo.',
  'admin.clientes.volver': 'Volver a clientes',
  'admin.clientes.formulario.general': 'Información general',
  'admin.clientes.formulario.tipo': 'Tipo',
  'admin.clientes.formulario.tipoAyuda': 'Persona natural o empresa. Determina qué documentos aplican.',
  'admin.clientes.formulario.codigo': 'Código',
  'admin.clientes.formulario.codigoAyuda': 'Se asigna solo. Puedes reemplazarlo.',
  'admin.clientes.formulario.nombre': 'Nombre',
  // Nombre comercial (#126): en Empresa, «Nombre» pasa a «Razón social».
  'admin.clientes.formulario.razonSocial': 'Razón social',
  'admin.clientes.formulario.nombreComercial': 'Nombre comercial',
  'admin.clientes.formulario.nombreComercialAyuda':
    'El nombre con el que se conoce a la empresa, si no es el de su razón social. También se busca por él. Opcional.',
  'admin.clientes.formulario.nombreComercialEjemplo': 'Ej. El Rayo',
  'admin.clientes.formulario.identificacion': 'Identificación',
  'admin.clientes.formulario.identificacionAyuda':
    'El documento con el que se identifica al cliente. Opcional; un mismo documento no puede ser de dos clientes.',
  'admin.clientes.formulario.tipoDocumento': 'Tipo de documento',
  'admin.clientes.formulario.tipoDocumentoAyuda': 'DUI, NIT, NRC según el país. Opcional.',
  'admin.clientes.formulario.sinDocumento': 'Sin documento',
  'admin.clientes.formulario.numeroDocumento': 'Número de documento',
  'admin.clientes.formulario.numeroDocumentoAyuda': 'El número tal como aparece en el documento.',
  'admin.clientes.formulario.contacto': 'Contacto',
  'admin.clientes.formulario.contactoAyuda': 'Cómo comunicarse con el cliente. Todos son opcionales.',
  'admin.clientes.formulario.telefono': 'Teléfono',
  'admin.clientes.formulario.telefonoAyuda': 'Preferido por WhatsApp si aplica.',
  'admin.clientes.formulario.correo': 'Correo electrónico',
  'admin.clientes.formulario.correoAyuda': 'Para enviar cotizaciones y recibos.',
  'admin.clientes.formulario.direccionYNotas': 'Dirección y notas',
  'admin.clientes.formulario.direccion': 'Dirección',
  'admin.clientes.formulario.notas': 'Notas',
  'admin.clientes.formulario.estado': 'Estado',
  'admin.clientes.formulario.activoAyuda':
    'Disponible para asignarle órdenes. Apágalo si el cliente ya no opera.',
  'admin.clientes.formulario.guardarCambios': 'Guardar cambios',
  // La línea bajo el título de la edición (#160), como en Productos.
  'admin.clientes.formulario.creado': 'Creado el {fecha}',
  'admin.clientes.formulario.modificado': 'Última modificación: {fecha}',
  'admin.clientes.formulario.modificadoPor': 'Última modificación: {fecha} por {autor}',
  'admin.clientes.formulario.hoy': 'hoy',
  'admin.clientes.formulario.eliminar': 'Eliminar cliente',
  'admin.clientes.contacto.ayuda': 'Teléfonos y correos del cliente. Marca uno principal por tipo.',
  'admin.clientes.contacto.tipo': 'Tipo',
  'admin.clientes.contacto.valor': 'Valor',
  'admin.clientes.contacto.etiqueta': 'Etiqueta',
  'admin.clientes.contacto.etiquetaEjemplo': 'Ej. Oficina',
  'admin.clientes.contacto.principal': 'Principal',
  'admin.clientes.contacto.telefono': 'Teléfono',
  'admin.clientes.contacto.correo': 'Correo',
  'admin.clientes.contacto.agregar': 'Agregar contacto',
  // Lo que lee el lector de pantalla en cada fila: la tabla no tiene etiquetas visibles por control.
  'admin.clientes.contacto.tipoDe': 'Tipo del contacto {n}',
  'admin.clientes.contacto.valorDe': 'Valor del contacto {n}',
  'admin.clientes.contacto.etiquetaDe': 'Etiqueta del contacto {n}',
  'admin.clientes.contacto.principalDe': 'Contacto {n} principal',
  'admin.clientes.contacto.quitar': 'Quitar el contacto {n}',
  'admin.clientes.error.titulo': 'No se pudo completar',
  'admin.clientes.error.repetido': 'El {tipo} {numero} ya está registrado a nombre de {nombre} ({codigo}).',
  'admin.clientes.error.verExistente': 'Ver cliente existente →',
  'admin.clientes.eliminar.titulo': 'Eliminar cliente',
  'admin.clientes.eliminar.texto':
    'Vas a eliminar a {nombre} ({codigo}). Dejará de aparecer en listados y búsquedas.',
  'admin.clientes.eliminar.boton': 'Eliminar cliente',
  // El rechazo de #177: `historial` es «14 órdenes y 3 activos», armado con las
  // dos de abajo y la conjunción del idioma; solo nombra lo que tiene.
  'admin.clientes.eliminar.bloqueado.titulo': 'No se puede eliminar',
  'admin.clientes.eliminar.bloqueado.activo':
    '{nombre} tiene {historial}. Eliminar es solo para un cliente registrado por error. Si ya no es tu cliente, desactívalo: sus órdenes y activos se conservan y no se le abren órdenes nuevas.',
  'admin.clientes.eliminar.bloqueado.inactivo':
    '{nombre} tiene {historial}. Eliminar es solo para un cliente registrado por error. Ya está inactivo: sus órdenes y activos se conservan y no se le abren órdenes nuevas.',
  'admin.clientes.eliminar.bloqueado.ordenes': { one: '1 orden', other: '{n} órdenes' },
  'admin.clientes.eliminar.bloqueado.activos': { one: '1 activo', other: '{n} activos' },
  'admin.clientes.eliminar.desactivar': 'Desactivar cliente',
  // «Desactivar cliente» desde la edición con campos cambiados (#177).
  'admin.clientes.eliminar.sinGuardar.titulo': 'Tienes cambios sin guardar',
  'admin.clientes.eliminar.sinGuardar.texto':
    '¿Quieres guardarlos antes de desactivar a {nombre}? Si no, se descartan.',
  'admin.clientes.eliminar.sinGuardar.descartar': 'Descartar y desactivar',
  'admin.clientes.eliminar.sinGuardar.guardar': 'Guardar y desactivar',
  'admin.clientes.eliminar.desactivarConflictoEdicion':
    'Otra persona cambió este cliente mientras tanto. Al cerrar se carga lo último: revísalo y vuelve a intentar.',
  'admin.clientes.eliminar.desactivarConflicto':
    'Otra persona cambió este cliente mientras tanto. Ya se cargó lo último: vuelve a intentar.',
  'admin.clientes.eliminar.entendido': 'Entendido',
  'admin.clientes.aviso.creado': 'Se creó el cliente {nombre}',
  'admin.clientes.aviso.guardado': 'Se guardaron los cambios de {nombre}',
  'admin.clientes.aviso.eliminado': 'Se eliminó el cliente {nombre}',
  'admin.clientes.aviso.activado': '{nombre} volvió a estar activo',
  'admin.clientes.aviso.desactivado': '{nombre} quedó inactivo',
  'admin.clientes.aviso.guardadoYDesactivado': 'Se guardaron los cambios de {nombre} y quedó inactivo',
  'admin.clientes.menu.verFicha': 'Ver ficha',

  // La importación (#106): «Importar clientes» y «Revisar importación» del Figma.
  'admin.clientes.importar.titulo': 'Importar clientes',
  'admin.clientes.importar.texto':
    'Sube un archivo con una fila por cliente. Las columnas son las de la plantilla: código, tipo, nombre, nombre comercial, documento, teléfono, correo, dirección y notas.',
  'admin.clientes.importar.plantilla': 'Descargar plantilla:',
  'admin.clientes.importar.plantillaExcel': 'Excel',
  'admin.clientes.importar.plantillaCsv': 'CSV',
  'admin.clientes.importar.zona': 'Arrastra el archivo aquí o',
  'admin.clientes.importar.elegir': 'elígelo',
  'admin.clientes.importar.cambiar': 'Cambiar',
  'admin.clientes.importar.formatos': 'Excel (.xlsx) o CSV, hasta 5 MB',
  'admin.clientes.importar.boton': 'Importar',
  'admin.clientes.importar.error.titulo': 'No se pudo importar',
  'admin.clientes.importar.cortada':
    'La importación se cortó a mitad y puede que una parte ya se haya guardado. Revisa el listado antes de volver a intentarlo: si importas el archivo de nuevo, los clientes sin código ni documento se repetirían.',
  'admin.clientes.importar.revisar.titulo': 'Revisar importación',
  'admin.clientes.importar.revisar.texto': {
    one: 'Se leyó 1 fila de {archivo}. Las filas con error no se importan: corrígelas en el archivo y vuelve a subirlo, o importa solo las válidas.',
    other:
      'Se leyeron {n} filas de {archivo}. Las filas con error no se importan: corrígelas en el archivo y vuelve a subirlo, o importa solo las válidas.',
  },
  'admin.clientes.importar.revisar.listos': {
    one: '1 cliente listo',
    other: '{n} clientes listos',
  },
  'admin.clientes.importar.revisar.conErrores': {
    one: '1 fila con errores',
    other: '{n} filas con errores',
  },
  // La caja gris sobre «Importar los N válidos» (#178): el aviso de los
  // duplicados se lee antes de importar, no después.
  'admin.clientes.importar.revisar.advertencia':
    'Si importas ahora las válidas, después sube solo las filas corregidas: el archivo entero repetiría los clientes sin código ni documento.',
  'admin.clientes.importar.revisar.boton': {
    one: 'Importar el válido',
    other: 'Importar los {n} válidos',
  },
  // Cuando la segunda revisión o un lote dejaron filas afuera. Reimportar el
  // archivo entero duplicaría los clientes sin código ni documento: se dice.
  'admin.clientes.importar.hecho.titulo': {
    one: 'Se importó 1 cliente',
    other: 'Se importaron {n} clientes',
  },
  'admin.clientes.importar.hecho.texto': {
    one: 'Esta fila no entró. Corrígela e impórtala sola: si vuelves a subir el archivo entero, los clientes sin código ni documento se repetirían.',
    other:
      'Estas {n} filas no entraron. Corrígelas e impórtalas solas: si vuelves a subir el archivo entero, los clientes sin código ni documento se repetirían.',
  },
  'admin.clientes.importar.entendido': 'Entendido',
  'admin.clientes.importar.aviso': {
    one: 'Se importó 1 cliente',
    other: 'Se importaron {n} clientes',
  },
  'admin.clientes.importar.columnaFila': 'Fila',
  'admin.clientes.importar.columnaNombre': 'Nombre',
  'admin.clientes.importar.columnaProblema': 'Problema',
  'admin.clientes.importar.columnaTipoDocumento': 'Tipo de documento',
  'admin.clientes.importar.columnaDireccion': 'Dirección',
  'admin.clientes.importar.columnaNotas': 'Notas',
  // «Correo: No es un correo válido (ana.hdez@)». La columna va primero porque el
  // texto del código es el genérico del formulario.
  'admin.clientes.importar.motivo': '{columna}: {texto}',
  'admin.clientes.importar.motivoConValor': '{columna}: {texto} ({valor})',

  // La ficha del cliente (#105). El nombre de la sección de activos sale de los
  // tipos del taller: «Vehículos» si solo trabaja con vehículos, «Equipos» si
  // solo con equipos, y «Activos» en cualquier otro caso. No se arma el plural
  // con el nombre del tipo: ese lo escribe el taller, y en español no se deja.
  'admin.clientes.ficha.migas': 'Ruta de navegación',
  'admin.clientes.ficha.info': 'Los datos del cliente, sus activos y sus últimas órdenes. Para corregir algo, usa «Editar cliente».',
  'admin.clientes.ficha.desde': 'Cliente desde el {fecha}',
  'admin.clientes.historial.info':
    'Quién registró al cliente, quién le cambió qué y cuándo se le agregó o se le quitó un activo, lo más nuevo arriba. De los datos personales —nombre, documento, notas, un teléfono o un correo— se ve que cambiaron, pero no a qué: no se guardan.',
  'admin.clientes.ficha.editar': 'Editar cliente',
  'admin.clientes.ficha.contacto.titulo': 'Contacto',
  'admin.clientes.ficha.contacto.texto': 'Datos de contacto del cliente. El principal aparece primero.',
  'admin.clientes.ficha.contacto.telefonoPrincipal': 'Teléfono principal',
  'admin.clientes.ficha.contacto.otrosTelefonos': 'Otros teléfonos',
  'admin.clientes.ficha.contacto.correoPrincipal': 'Correo principal',
  'admin.clientes.ficha.contacto.otrosCorreos': 'Otros correos',
  'admin.clientes.ficha.contacto.direccion': 'Dirección',
  'admin.clientes.ficha.contacto.vacio': 'Este cliente todavía no tiene datos de contacto.',
  'admin.clientes.ficha.activos.vehiculos': 'Vehículos ({n})',
  'admin.clientes.ficha.activos.equipos': 'Equipos ({n})',
  'admin.clientes.ficha.activos.activos': 'Activos ({n})',
  'admin.clientes.ficha.activos.texto':
    'Activos registrados a nombre del cliente. Sobre ellos se abren las órdenes.',
  'admin.clientes.ficha.activos.agregar.vehiculos': 'Agregar vehículo',
  'admin.clientes.ficha.activos.agregar.equipos': 'Agregar equipo',
  'admin.clientes.ficha.activos.agregar.activos': 'Agregar activo',
  'admin.clientes.ficha.activos.vacio.vehiculos': 'Este cliente aún no tiene vehículos',
  'admin.clientes.ficha.activos.vacio.equipos': 'Este cliente aún no tiene equipos',
  'admin.clientes.ficha.activos.vacio.activos': 'Este cliente aún no tiene activos',
  'admin.clientes.ficha.activos.vacioTexto': 'Agrega el primero para poder abrirle órdenes.',
  'admin.clientes.ficha.activos.columna.codigo': 'Código',
  'admin.clientes.ficha.activos.columna.marcaModelo': 'Marca / Modelo',
  'admin.clientes.ficha.activos.columna.tipo': 'Tipo',
  'admin.clientes.ficha.activos.columna.identificador': 'Identificador',
  'admin.clientes.ficha.activos.columna.anio': 'Año',
  'admin.clientes.ficha.activos.columna.ordenes': 'Órdenes',
  'admin.clientes.ficha.activos.columna.estado': 'Estado',
  'admin.clientes.ficha.ordenes.titulo': 'Últimas órdenes',
  'admin.clientes.ficha.ordenes.texto': 'Las más recientes del cliente, de cualquier sucursal.',
  'admin.clientes.ficha.ordenes.vacio': 'Aún no hay órdenes para este cliente',
  'admin.clientes.ficha.ordenes.vacioTexto': 'Aparecerán aquí cuando se abra la primera.',
  'admin.clientes.ficha.ordenes.sinFolio': 'Sin folio',
  'admin.clientes.ficha.ordenes.columna.folio': 'Folio',
  'admin.clientes.ficha.ordenes.columna.vehiculos': 'Vehículo',
  'admin.clientes.ficha.ordenes.columna.equipos': 'Equipo',
  'admin.clientes.ficha.ordenes.columna.activos': 'Activo',
  'admin.clientes.ficha.ordenes.columna.sucursal': 'Sucursal',
  'admin.clientes.ficha.ordenes.columna.estado': 'Estado',
  'admin.clientes.ficha.ordenes.columna.abierta': 'Abierta',
  'admin.clientes.ficha.ordenes.columna.total': 'Total',
  'admin.clientes.ficha.notas': 'Notas',

  // Solo del formulario, no de la API: cuando falta el nombre, la API dice
  // `validation.required`, y la pantalla dice qué escribir según la clase.
  'error.cliente.nombre_persona': 'Escribe el nombre de la persona',
  'error.cliente.nombre_empresa': 'Escribe la razón social de la empresa',
  'error.catalogo.nombre_servicio': 'Escribe el nombre del servicio',
  'error.catalogo.nombre_producto': 'Escribe el nombre del producto',
  'error.catalogo.numero_invalido': 'Escribe un número, como 45.50',

  // --- Marcas y modelos (M-7) ---
  //
  // Sin frame en el Figma: una pantalla de dos columnas, con las ventanas de
  // alta con el molde de «Nueva categoría» de Productos.
  'admin.marcas.titulo': 'Marcas y modelos',
  'admin.marcas.info': 'Las marcas y los modelos que se eligen al registrar un activo. Elige una marca para ver sus modelos. Nada se elimina: lo que ya no se usa se desactiva y deja de ofrecerse.',
  'admin.marcas.columnaMarcas': 'Marcas',
  'admin.marcas.columnaModelos': 'Modelos',
  'admin.marcas.columnaModelosDe': 'Modelos de {marca}',
  'admin.marcas.nuevaMarca': 'Nueva marca',
  'admin.marcas.nuevoModelo': 'Nuevo modelo',
  'admin.marcas.buscar': 'Buscar marca',
  'admin.marcas.filtro.tipo': 'Tipo',
  'admin.marcas.filtro.todos': 'Todos',
  'admin.marcas.filtro.estado': 'Estado',
  'admin.marcas.filtro.todas': 'Todas',
  'admin.marcas.activa': 'Activa',
  'admin.marcas.inactiva': 'Inactiva',
  'admin.marcas.inactivo': 'Inactivo',
  'admin.marcas.conteoModelos': { one: '1 modelo', other: '{n} modelos' },
  'admin.marcas.vacio.titulo': 'Aún no hay marcas',
  'admin.marcas.vacio.texto':
    'Las marcas y sus modelos son lo que se elige al registrar un vehículo o un equipo. Agrega las que más atiendes; las demás se pueden crear al registrar el activo.',
  'admin.marcas.eligeUnaMarca': 'Elige una marca para ver sus modelos.',
  'admin.marcas.sinModelos': '{marca} aún no tiene modelos.',

  'admin.marcas.menu.renombrar': 'Cambiar nombre',
  'admin.marcas.menu.desactivar': 'Desactivar',
  'admin.marcas.menu.activar': 'Activar',

  'admin.marcas.nombre': 'Nombre',
  'admin.marcas.guardar': 'Guardar',
  'admin.marcas.tipo': 'Tipo de activo',
  'admin.marcas.tipo.generica': 'Todos los tipos',
  'admin.marcas.tipo.ayuda':
    'Si la marca solo hace un tipo de activo, elígelo: así no aparece al registrar los demás. No se puede cambiar después.',
  'admin.marcas.ventanaMarca.nueva': 'Nueva marca',
  'admin.marcas.ventanaMarca.nuevaTexto': 'Se guarda en el catálogo del taller.',
  'admin.marcas.ventanaMarca.renombrar': 'Cambiar nombre de la marca',
  'admin.marcas.ventanaMarca.renombrarTexto': 'Los activos que ya la tienen muestran el nombre nuevo.',
  'admin.marcas.ventanaMarca.ejemplo': 'Ej. Toyota',
  'admin.marcas.ventanaMarca.crear': 'Guardar marca',
  'admin.marcas.parecida.deTipo':
    'Ya existe «{nombre}» para {tipo}. Si es la misma marca, no hace falta crear otra.',
  'admin.marcas.parecida.generica':
    'Ya existe «{nombre}» para todos los tipos. Si es la misma marca, no hace falta crear otra.',
  'admin.marcas.ventanaModelo.nuevo': 'Nuevo modelo',
  'admin.marcas.ventanaModelo.nuevoTexto': 'Se agrega a los modelos de {marca}.',
  'admin.marcas.ventanaModelo.renombrar': 'Cambiar nombre del modelo',
  'admin.marcas.ventanaModelo.ejemplo': 'Ej. Hilux',
  'admin.marcas.ventanaModelo.crear': 'Guardar modelo',

  'admin.marcas.aviso.marcaCreada': 'Se creó la marca {nombre}',
  'admin.marcas.aviso.marcaRenombrada': 'La marca ahora se llama {nombre}',
  'admin.marcas.aviso.marcaDesactivada': '{nombre} ya no se ofrece al registrar activos',
  'admin.marcas.aviso.marcaActivada': '{nombre} se vuelve a ofrecer',
  'admin.marcas.aviso.modeloCreado': 'Se creó el modelo {nombre}',
  'admin.marcas.aviso.modeloRenombrado': 'El modelo ahora se llama {nombre}',
  'admin.marcas.aviso.modeloDesactivado': '{nombre} ya no se ofrece al registrar activos',
  'admin.marcas.aviso.modeloActivado': '{nombre} se vuelve a ofrecer',
  'admin.marcas.error.titulo': 'No se pudo completar',

  // --- Estados de la orden (#145) ---
  //
  // «Etapa» es la palabra de la pantalla para la categoría del estado: lo único
  // que el sistema entiende de él.
  'admin.estados.titulo': 'Estados de la orden',
  'admin.estados.info':
    'Los pasos por los que avanza una orden, agrupados por etapa. Cada taller los nombra como quiere; la etapa es lo que el sistema entiende: entregar pide cliente y folio, anular pide motivo. Nada se elimina: lo que ya no se usa se desactiva, y las órdenes que están en él se quedan ahí.',
  'admin.estados.etapa.open': 'Abierta',
  'admin.estados.etapa.inProgress': 'En curso',
  'admin.estados.etapa.completed': 'Terminada',
  'admin.estados.etapa.closed': 'Entregada',
  'admin.estados.etapa.cancelled': 'Anulada',
  'admin.estados.etapa.open.info':
    'Recibida y sin trabajar todavía. El estado inicial, con el que nace toda orden, es de esta etapa.',
  'admin.estados.etapa.inProgress.info': 'Con trabajo en marcha: diagnóstico, autorizada, en proceso.',
  'admin.estados.etapa.completed.info':
    'El trabajo está hecho y falta entregar. Aquí decides qué estados congelan los montos: al llegar a uno, los precios de la orden ya no cambian.',
  'admin.estados.etapa.closed.info':
    'Entregada y sin nada pendiente. Entregar pide cliente y folio. Siempre congela los montos.',
  'admin.estados.etapa.cancelled.info': 'La orden no siguió. Anular pide motivo. Siempre congela los montos.',
  'admin.estados.etapaVacia': 'Esta etapa no tiene estados.',
  'admin.estados.agregar': 'Agregar estado',
  'admin.estados.inicial': 'Inicial',
  'admin.estados.congela': 'Congela montos',
  'admin.estados.inactivo': 'Inactivo',
  'admin.estados.conteoOrdenes': { one: '1 orden', other: '{n} órdenes' },
  'admin.estados.subir': 'Subir {nombre}',
  'admin.estados.bajar': 'Bajar {nombre}',
  'admin.estados.menu.editar': 'Editar',
  'admin.estados.menu.inicial': 'Nombrar inicial',
  'admin.estados.menu.desactivar': 'Desactivar',
  'admin.estados.menu.activar': 'Activar',
  'admin.estados.ventana.nuevo': 'Nuevo estado',
  'admin.estados.ventana.nuevoTexto': 'Nace activo, al final de su etapa.',
  'admin.estados.ventana.editar': 'Editar estado',
  'admin.estados.ventana.editarTexto':
    'Las órdenes que ya están en este estado muestran los cambios. Si le cambias la etapa, pasa al final de la nueva.',
  'admin.estados.campo.nombre': 'Nombre',
  'admin.estados.campo.ejemplo': 'Ej. Esperando repuesto',
  'admin.estados.campo.etapa': 'Etapa',
  'admin.estados.campo.etapa.info':
    'Lo que el sistema entiende del estado: si la orden está por empezar, en curso, terminada, entregada o anulada.',
  'admin.estados.campo.etapaFija': 'Etapa: {etapa}',
  'admin.estados.campo.etapaFija.info': {
    one: 'Tiene 1 orden: la etapa y si congela los montos no se cambian. Muévela a otro estado, o crea uno nuevo en la etapa que quieres.',
    other:
      'Tiene {n} órdenes: la etapa y si congela los montos no se cambian. Muévelas a otro estado, o crea uno nuevo en la etapa que quieres.',
  },
  'admin.estados.campo.color': 'Color',
  'admin.estados.campo.congela': 'Congela los montos',
  'admin.estados.campo.congela.info':
    'Al llegar a este estado, los precios de las líneas y el total de la orden ya no cambian.',
  'admin.estados.color.sin': 'Sin color',
  'admin.estados.color.gris': 'Gris',
  'admin.estados.color.azul': 'Azul',
  'admin.estados.color.celeste': 'Celeste',
  'admin.estados.color.verde': 'Verde',
  'admin.estados.color.amarillo': 'Amarillo',
  'admin.estados.color.naranja': 'Naranja',
  'admin.estados.color.rojo': 'Rojo',
  'admin.estados.color.morado': 'Morado',
  'admin.estados.guardar': 'Guardar',
  'admin.estados.crear': 'Guardar estado',
  'admin.estados.aviso.creado': 'Se creó el estado {nombre}',
  'admin.estados.aviso.editado': 'Se guardó {nombre}',
  'admin.estados.aviso.inicial': 'Las órdenes nuevas nacen en {nombre}',
  'admin.estados.aviso.desactivado': '{nombre} ya no se ofrece para mover órdenes',
  'admin.estados.aviso.activado': '{nombre} se vuelve a ofrecer',
  'admin.estados.aviso.reordenado': 'Se guardó el orden',
  'admin.estados.error.titulo': 'No se pudo completar',

  // --- Catálogos (#154) ---
  'admin.catalogos.titulo': 'Catálogos',
  'admin.catalogos.info':
    'Las listas que se eligen en los formularios: las categorías de productos y servicios, las especialidades del personal y los tipos de activo con sus campos. Lo que ya se usa no se elimina: se desactiva y deja de ofrecerse.',
  'admin.catalogos.lista.titulo': 'Catálogos',
  'admin.catalogos.lista.texto': 'Elige uno para ver sus ítems',
  'admin.catalogos.sinPermiso': 'Tu rol no puede mantener ninguno de estos catálogos.',
  'admin.catalogos.error.titulo': 'No se pudo hacer el cambio',
  'admin.catalogos.buscar': 'Buscar por nombre',
  'admin.catalogos.filtro.estado': 'Estado',
  'admin.catalogos.filtro.todos': 'Todos',
  'admin.catalogos.filtro.activos': 'Activos',
  'admin.catalogos.filtro.inactivos': 'Inactivos',
  'admin.catalogos.inactiva': 'Inactiva',
  'admin.catalogos.inactivo': 'Inactivo',
  'admin.catalogos.inactivas': { one: '1 inactiva', other: '{n} inactivas' },
  'admin.catalogos.inactivos': { one: '1 inactivo', other: '{n} inactivos' },
  'admin.catalogos.sinUsar': 'Sin usar',
  'admin.catalogos.nombre': 'Nombre',
  'admin.catalogos.guardarCambios': 'Guardar cambios',
  'admin.catalogos.eliminar': 'Eliminar',
  'admin.catalogos.menu.editar': 'Editar',
  'admin.catalogos.menu.campos': 'Campos',
  'admin.catalogos.menu.desactivar': 'Desactivar',
  'admin.catalogos.menu.activar': 'Activar',
  'admin.catalogos.menu.eliminar': 'Eliminar',
  'admin.catalogos.categorias.titulo': 'Categorías de productos y servicios',
  'admin.catalogos.categorias.conteo': { one: '1 categoría', other: '{n} categorías' },
  'admin.catalogos.categorias.usada': { one: 'Usada en 1', other: 'Usada en {n}' },
  'admin.catalogos.categorias.nueva': 'Nueva categoría',
  'admin.catalogos.categorias.editar': 'Editar categoría',
  'admin.catalogos.categorias.ventanaInfo':
    'Agrupa los productos y servicios en el filtro y en el combo de Productos/Servicios.',
  'admin.catalogos.categorias.ejemplo': 'Ej.: Frenos',
  'admin.catalogos.categorias.vacio': 'Aún no hay categorías',
  'admin.catalogos.categorias.aviso.creada': 'Se creó la categoría {nombre}',
  'admin.catalogos.categorias.aviso.guardada': 'Se guardó la categoría {nombre}',
  'admin.catalogos.categorias.aviso.desactivada': 'Se desactivó la categoría {nombre}',
  'admin.catalogos.categorias.aviso.activada': 'Se activó la categoría {nombre}',
  'admin.catalogos.categorias.aviso.eliminada': 'Se eliminó la categoría {nombre}',
  'admin.catalogos.categorias.eliminar.titulo': '¿Eliminar la categoría {nombre}?',
  'admin.catalogos.categorias.eliminar.texto':
    'No está en ningún producto ni servicio, así que no se pierde nada. Su nombre queda libre para volver a usarse.',
  'admin.catalogos.especialidades.titulo': 'Especialidades',
  'admin.catalogos.especialidades.conteo': { one: '1 especialidad', other: '{n} especialidades' },
  'admin.catalogos.especialidades.usada': { one: 'La tiene 1 persona', other: 'La tienen {n} personas' },
  'admin.catalogos.especialidades.nueva': 'Nueva especialidad',
  'admin.catalogos.especialidades.editar': 'Editar especialidad',
  'admin.catalogos.especialidades.ventanaInfo':
    'Lo que sabe hacer cada persona del taller. Se le asigna en su ficha; también nace al escribirla ahí.',
  'admin.catalogos.especialidades.ejemplo': 'Ej.: Suspensión',
  'admin.catalogos.especialidades.vacio': 'Aún no hay especialidades',
  'admin.catalogos.especialidades.aviso.creada': 'Se creó la especialidad {nombre}',
  'admin.catalogos.especialidades.aviso.guardada': 'Se guardó la especialidad {nombre}',
  'admin.catalogos.especialidades.aviso.desactivada': 'Se desactivó la especialidad {nombre}',
  'admin.catalogos.especialidades.aviso.activada': 'Se activó la especialidad {nombre}',
  'admin.catalogos.especialidades.aviso.eliminada': 'Se eliminó la especialidad {nombre}',
  'admin.catalogos.especialidades.eliminar.titulo': '¿Eliminar la especialidad {nombre}?',
  'admin.catalogos.especialidades.eliminar.texto':
    'Ninguna ficha la tiene, así que no se pierde nada. Su nombre queda libre para volver a usarse.',
  'admin.catalogos.tipos.titulo': 'Tipos de activo',
  'admin.catalogos.tipos.conteo': { one: '1 tipo', other: '{n} tipos' },
  'admin.catalogos.tipos.usado': { one: 'Usado en 1', other: 'Usado en {n}' },
  'admin.catalogos.tipos.vacio': 'Aún no hay tipos de activo',
  'admin.catalogos.tipos.camposDe': 'Campos de {tipo}',

  // --- Tipos de activo (#117) ---
  //
  // Las ventanas del tipo y de sus campos, y el panel de campos, que desde #154
  // se abren en Catálogos. Las claves técnicas no se muestran nunca: las arma el
  // sistema.
  'admin.tipos.nuevo': 'Nuevo tipo',
  'admin.tipos.menu.editarCampo': 'Editar',
  'admin.tipos.menu.apagar': 'Apagar',
  'admin.tipos.menu.encender': 'Encender',
  'admin.tipos.resumen.exige': 'Se identifica por «{identificador}», que es obligatorio.',
  'admin.tipos.resumen.opcional': 'Se identifica por «{identificador}», que es opcional.',
  'admin.tipos.campos.titulo': 'Campos',
  'admin.tipos.campos.texto':
    'Lo que el formulario pide además de marca, modelo, año e identificador, en este orden.',
  'admin.tipos.campos.vacio':
    'Este tipo aún no tiene campos: el formulario pedirá solo marca, modelo, año e identificador.',
  'admin.tipos.agregarCampo': 'Agregar campo',
  'admin.tipos.obligatorio': 'Obligatorio',
  'admin.tipos.apagado': 'Apagado',
  'admin.tipos.conteoOpciones': { one: '1 opción', other: '{n} opciones' },
  'admin.tipos.subir': 'Subir {nombre}',
  'admin.tipos.bajar': 'Bajar {nombre}',
  'admin.tipos.guardar': 'Guardar',

  'admin.tipos.nombre': 'Nombre',
  'admin.tipos.nombreEjemplo': 'Ej. Compresor',
  'admin.tipos.identificador': 'Cómo se identifica',
  'admin.tipos.identificadorEjemplo': 'Ej. Serie',
  'admin.tipos.identificadorAyuda': 'El nombre del dato que distingue a cada uno: «Placa», «Serie», «N.º de ticket».',
  'admin.tipos.exigeIdentificador': 'Exigir el identificador',
  'admin.tipos.exigeIdentificadorAyuda':
    'Si lo enciendes, no se registra un activo de este tipo sin él. Los que ya existen no cambian.',
  'admin.tipos.ventanaTipo.nuevoTexto': 'Después le agregas los campos que necesite.',
  'admin.tipos.ventanaTipo.editar': 'Editar tipo de activo',
  'admin.tipos.ventanaTipo.editarTexto': 'Los activos de este tipo muestran los nombres nuevos.',
  'admin.tipos.ventanaTipo.crear': 'Crear tipo',

  'admin.tipos.ventanaCampo.nuevo': 'Agregar campo',
  'admin.tipos.ventanaCampo.editar': 'Editar campo',
  'admin.tipos.ventanaCampo.texto': 'Un dato que se pide al registrar un activo de tipo {tipo}.',
  'admin.tipos.ventanaCampo.crear': 'Agregar campo',
  'admin.tipos.campo.etiqueta': 'Nombre del campo',
  'admin.tipos.campo.etiquetaEjemplo': 'Ej. Kilometraje',
  'admin.tipos.campo.dato': 'Tipo de dato',
  'admin.tipos.campo.datoAyuda': 'No se puede cambiar después: los activos guardan el dato con este tipo.',
  'admin.tipos.campo.datoFijo': 'Tipo de dato: {dato}. No se cambia una vez creado.',
  'admin.tipos.campo.obligatorio': 'Obligatorio',
  'admin.tipos.campo.obligatorioAyuda':
    'Úsalo poco: cada campo obligatorio es un paso más para quien registra el activo de pie.',
  'admin.tipos.dato.text': 'Texto',
  'admin.tipos.dato.number': 'Número',
  'admin.tipos.dato.bool': 'Sí / No',
  'admin.tipos.dato.date': 'Fecha',
  'admin.tipos.dato.list': 'Lista de opciones',
  'admin.tipos.opciones.titulo': 'Opciones',
  'admin.tipos.opciones.nombre': 'Opción {n}',
  'admin.tipos.opciones.quitar': 'Quitar {nombre}',
  'admin.tipos.opciones.nueva': 'Nueva opción',
  'admin.tipos.opciones.placeholder': 'Escribe una opción y presiona Enter',
  'admin.tipos.opciones.agregar': 'Agregar',
  'admin.tipos.opciones.ayuda': 'Las que se podrán elegir. Puedes cambiarles el nombre después.',
  'admin.tipos.opciones.repetida': '«{nombre}» ya está en la lista.',
  'admin.tipos.opciones.quitadas':
    'Los activos que ya tienen {nombres} lo conservan hasta que alguien les elija otra opción.',

  'admin.tipos.aviso.creado': 'Se creó el tipo {nombre}',
  'admin.tipos.aviso.guardado': 'Se guardaron los cambios de {nombre}',
  'admin.tipos.aviso.activado': '{nombre} se vuelve a ofrecer',
  'admin.tipos.aviso.desactivado': '{nombre} ya no se ofrece para activos nuevos',
  'admin.tipos.aviso.campoAgregado': 'Se agregó el campo {nombre}',
  'admin.tipos.aviso.campoGuardado': 'Se guardó el campo {nombre}',
  'admin.tipos.aviso.campoApagado': '{nombre} ya no se pide; lo que los activos tenían se conserva',
  'admin.tipos.aviso.campoEncendido': '{nombre} se vuelve a pedir',
  'admin.tipos.aviso.reordenado': 'Se guardó el orden de los campos',
  'admin.tipos.error.titulo': 'No se pudo completar',

  // --- Adjuntos (B-26): la galería de fotos y archivos de un activo ---
  'admin.adjuntos.titulo': 'Fotos y archivos',
  'admin.adjuntos.agregar': 'Agregar',
  'admin.adjuntos.subiendo': {
    one: 'Subiendo 1 archivo…',
    other: 'Subiendo {n} archivos…',
  },
  'admin.adjuntos.cargando': 'Cargando…',
  'admin.adjuntos.vacio': 'Todavía no hay fotos ni archivos.',
  'admin.adjuntos.errorLista': 'No se pudieron leer los adjuntos. Vuelve a intentar en un momento.',
  'admin.adjuntos.tamano': '{kb} KB',
  'admin.adjuntos.eliminar': 'Eliminar {nombre}',
  'admin.adjuntos.aviso.eliminado': 'Se eliminó {nombre}',
  'admin.adjuntos.error.titulo': 'No se pudo completar',
  'admin.adjuntos.cuantas': {
    one: '{n} foto',
    other: '{n} fotos',
  },

  // --- Gastos (#143): «Gastos de esta orden» en la ficha, y su ventana ---
  'admin.gastos.titulo': 'Gastos de esta orden',
  'admin.gastos.info':
    'Lo que se compró para este trabajo: repuestos, materiales, un servicio de afuera. El proveedor, la factura y el monto se pueden completar después.',
  'admin.gastos.registrar': 'Registrar gasto',
  'admin.gastos.vacio': 'Aún no hay gastos en esta orden',
  'admin.gastos.vacioTexto': 'Registra lo que se compró para este trabajo, con la foto de la factura.',
  'admin.gastos.noSePudo': 'No se pudo cambiar el gasto',
  'admin.gastos.hoy': 'hoy',
  'admin.gastos.factura': 'Fact. {numero}',
  'admin.gastos.registro': 'registró {persona}',
  'admin.gastos.pendiente': 'Pendiente de pago',
  'admin.gastos.pagado': 'Pagado {fecha}',
  'admin.gastos.sinMonto': 'Sin monto',
  'admin.gastos.acciones': 'Acciones de {gasto}',
  'admin.gastos.editar': 'Editar',
  'admin.gastos.cambiarMonto': 'Cambiar monto',
  'admin.gastos.marcarPagado': 'Marcar pagado',
  'admin.gastos.quitarPago': 'Quitar marca de pagado',
  'admin.gastos.eliminar': 'Eliminar',
  'admin.gastos.formulario.nuevo': 'Registrar gasto',
  'admin.gastos.formulario.editar': 'Editar gasto',
  'admin.gastos.formulario.info':
    'Con la descripción y la fecha alcanza. El proveedor, la factura y el monto los puede completar después quien lleva la caja.',
  'admin.gastos.formulario.registradoPor': 'Lo registró {persona}.',
  'admin.gastos.formulario.descripcion': 'Descripción',
  'admin.gastos.formulario.descripcionEjemplo': 'Pastillas y líquido de frenos',
  'admin.gastos.formulario.fecha': 'Fecha de compra',
  'admin.gastos.formulario.proveedor': 'Proveedor',
  'admin.gastos.formulario.sinProveedor': 'Sin proveedor',
  'admin.gastos.formulario.nuevoProveedor': 'Nuevo proveedor',
  'admin.gastos.formulario.factura': 'N.º de factura',
  'admin.gastos.formulario.monto': 'Monto',
  'admin.gastos.formulario.montoInfo': 'Lo que costó, como dice la factura. Vacío, queda sin monto.',
  'admin.gastos.formulario.notas': 'Notas',
  'admin.gastos.comprobante.titulo': 'Comprobante',
  'admin.gastos.comprobante.ninguno': 'Sin foto todavía.',
  'admin.gastos.comprobante.quitar': 'Quitar {nombre}',
  'admin.gastos.alta.incompleto': 'El gasto se registró, pero no todo',
  'admin.gastos.alta.montoDespues': 'Cámbialo desde «Cambiar monto», en el «•••» del gasto.',
  'admin.gastos.alta.cerrar': 'Cerrar',
  'admin.gastos.pago.fecha': 'Fecha de pago',
  'admin.gastos.pago.info': 'El día en que se pagó. No puede ser anterior a la compra.',
  'admin.gastos.eliminar.titulo': 'Eliminar gasto',
  'admin.gastos.eliminar.texto':
    'Vas a eliminar «{gasto}». Se oculta junto con su comprobante, y su número de factura queda libre para otro gasto.',
  'admin.gastos.aviso.registrado': 'Gasto registrado',
  'admin.gastos.aviso.guardado': 'Cambios guardados',
  'admin.gastos.aviso.pagado': 'Gasto marcado como pagado',
  'admin.gastos.aviso.pagoQuitado': 'El gasto vuelve a estar pendiente de pago',
  'admin.gastos.aviso.eliminado': 'Gasto eliminado',

  // --- La ficha del gasto (#189): llegó con el historial ---
  'admin.gastos.ficha.info':
    'Lo que se compró, a quién y si ya se pagó, con su comprobante y quién lo cambió. Para corregir algo, usa «Editar gasto».',
  'admin.gastos.ficha.compra': 'Compra {fecha}',
  'admin.gastos.ficha.datos': 'Datos del gasto',
  'admin.gastos.ficha.datosInfo':
    'El monto, el proveedor y la factura los completa quien lleva la caja. Mientras falte el monto o el proveedor, el gasto sale en «Por completar» del listado de gastos.',
  'admin.gastos.ficha.sucursal': 'Sucursal',
  'admin.gastos.ficha.comprobanteInfo':
    'La foto de la factura o del recibo, para revisar el monto sin buscar el papel.',
  'admin.gastos.mas': 'Más acciones',
  'admin.gastos.verFicha': 'Ver ficha',
  'admin.gastos.noExiste.titulo': 'Este gasto no existe',
  'admin.gastos.noExiste.texto': 'Puede que lo hayan eliminado. Vuelve a la orden o al listado de gastos.',
  'admin.gastos.volver': 'Volver',
  'admin.gastos.historial.info':
    'Quién registró el gasto, quién le cambió qué, cuándo se pagó y cuándo se agregó o se quitó el comprobante, lo más nuevo arriba. Las cifras del monto solo las ves si tienes permiso de ver montos; si no, dice que cambió el monto sin decir de cuánto a cuánto.',
  'admin.gastos.historial.pagadoEl': 'Pagado el {fecha}',
  'admin.gastos.historial.estabaPagado': 'Estaba pagado el {fecha}',
  // El comprobante (#195), sea foto o PDF.
  'admin.gastos.historial.comprobanteAgregado': 'Comprobante agregado',
  'admin.gastos.historial.comprobanteEliminado': 'Comprobante eliminado',

  // --- El listado de la caja (#144) ---
  'admin.gastos.listado.titulo': 'Gastos',
  'admin.gastos.listado.info':
    'Lo que falta pagar y lo que falta completar —el monto o el proveedor— de los gastos de esta sucursal, de las órdenes y del taller. Toca un gasto para ver su ficha y completarlo.',
  'admin.gastos.listado.sinSucursal': 'Elige una sucursal arriba para ver sus gastos.',
  'admin.gastos.listado.filtro': 'Mostrar',
  'admin.gastos.listado.porPagar': 'Pendientes de pago',
  'admin.gastos.listado.porCompletar': 'Por completar',
  'admin.gastos.listado.todos': 'Todos',
  'admin.gastos.listado.columna.gasto': 'Gasto',
  'admin.gastos.listado.columna.orden': 'Orden',
  'admin.gastos.listado.columna.compra': 'Compra',
  'admin.gastos.listado.columna.pago': 'Pago',
  'admin.gastos.listado.delTaller': 'Del taller',
  'admin.gastos.listado.falta': 'Falta: {que}',
  'admin.gastos.listado.falta.monto': 'el monto',
  'admin.gastos.listado.falta.proveedor': 'el proveedor',
  'admin.gastos.listado.vacio.por-pagar': 'No hay gastos pendientes de pago',
  'admin.gastos.listado.vacio.por-completar': 'No hay gastos por completar',
  'admin.gastos.listado.vacio.todos': 'Aún no hay gastos en esta sucursal',
  'admin.gastos.listado.paginaVacia': 'Lo que había en esta página ya se completó o se pagó.',
  'admin.gastos.listado.vacio.texto':
    'Los gastos se registran desde la ficha de una orden, o aquí si son del taller: la renta, la luz.',

  // --- Activos (#119) ---
  //
  // Sin frame en el Figma: el listado sigue el molde de Sucursales y el
  // formulario se arma con los campos que declara el tipo de activo.
  'admin.activos.titulo': 'Activos',
  'admin.activos.info': 'Lo que los clientes traen al taller: sus carros, sus equipos. Sobre un activo se abren las órdenes.',
  'admin.activos.nuevo': 'Nuevo activo',
  'admin.activos.nuevo.descripcion':
    'Elige el tipo y aparecen sus datos. Solo lo marcado con * es obligatorio: lo demás se completa después.',
  'admin.activos.editar.titulo': 'Editar activo',
  'admin.activos.editar.info': 'Corrige los datos del activo. Los campos marcados con * son obligatorios.',
  'admin.activos.buscar': 'Buscar por placa, serie o código',
  'admin.activos.filtro.tipo': 'Tipo',
  'admin.activos.filtro.estado': 'Estado',
  'admin.activos.filtro.todos': 'Todos',
  'admin.activos.activo': 'Activo',
  'admin.activos.inactivo': 'Inactivo',
  'admin.activos.columna.codigo': 'Código',
  'admin.activos.columna.identificador': 'Identificador',
  'admin.activos.columna.tipo': 'Tipo',
  'admin.activos.columna.marcaModelo': 'Marca / Modelo',
  'admin.activos.columna.anio': 'Año',
  'admin.activos.columna.cliente': 'Cliente',
  'admin.activos.columna.estado': 'Estado',
  'admin.activos.vacio.titulo': 'Aún no hay activos',
  'admin.activos.vacio.texto':
    'Un activo es lo que el cliente trae al taller: su carro, su equipo. Sobre él se abren las órdenes.',
  'admin.activos.sinResultados': 'Ningún activo coincide con «{texto}».',
  'admin.activos.menu.verFicha': 'Ver ficha',
  'admin.activos.menu.editar': 'Editar',
  'admin.activos.menu.activar': 'Activar',
  'admin.activos.menu.desactivar': 'Desactivar',
  'admin.activos.menu.eliminar': 'Eliminar',
  'admin.activos.noExiste.titulo': 'Este activo no existe',
  'admin.activos.noExiste.texto': 'Puede que lo hayan eliminado. Vuelve al listado para ver los activos del taller.',
  'admin.activos.volver': 'Volver a activos',
  'admin.activos.guardarCambios': 'Guardar cambios',
  // La ficha (#187): llegó con el historial.
  'admin.activos.ficha.info':
    'Los datos del activo, de quién es y lo que pide su tipo. Para corregir algo, usa «Editar activo».',
  'admin.activos.ficha.sinCliente': 'Sin cliente',
  'admin.activos.mas': 'Más acciones',
  'admin.activos.historial.info':
    'Quién registró el activo, quién le cambió qué y quién le agregó o eliminó fotos y archivos, lo más nuevo arriba. De la placa, el código, las notas y los detalles de texto se ve que cambiaron, pero no a qué: no se guardan.',

  'admin.activos.seccion.general': 'Información general',
  'admin.activos.seccion.generalTexto': 'Qué es y cómo se reconoce.',
  'admin.activos.seccion.marca': 'Marca y modelo',
  'admin.activos.seccion.marcaTexto': 'Si no está en la lista, créala aquí mismo.',
  'admin.activos.seccion.cliente': 'Cliente',
  'admin.activos.seccion.clienteTexto': 'De quién es. Opcional: se puede asignar después.',
  'admin.activos.seccion.detalles': 'Detalles',
  'admin.activos.seccion.detallesTexto': 'Los datos que pide el tipo {tipo}.',
  'admin.activos.seccion.notas': 'Notas',
  'admin.activos.tipo': 'Tipo de activo',
  'admin.activos.tipo.elegir': 'Elige el tipo',
  'admin.activos.codigo': 'Código',
  'admin.activos.codigoAyuda': 'Se asigna solo. Puedes reemplazarlo.',
  'admin.activos.identificador': 'Identificador',
  'admin.activos.anio': 'Año',
  'admin.activos.marca': 'Marca',
  'admin.activos.modelo': 'Modelo',
  'admin.activos.sinMarca': 'Sin marca',
  'admin.activos.sinModelo': 'Sin modelo',
  'admin.activos.modelo.antesMarca': 'Elige primero la marca',
  'admin.activos.nuevaMarca': 'Nueva marca',
  'admin.activos.nuevoModelo': 'Nuevo modelo',
  'admin.activos.cliente': 'Cliente',
  'admin.activos.cliente.buscar': 'Escribe el nombre o el documento',
  'admin.activos.cliente.ayuda': 'Escribe al menos dos letras para buscar.',
  'admin.activos.cliente.sinResultados': 'Ningún cliente coincide.',
  'admin.activos.cliente.resultados': 'Clientes que coinciden',
  'admin.activos.cliente.cambiar': 'Cambiar',
  'admin.activos.sinCampos': 'Este tipo no pide datos propios: basta con lo de arriba.',
  'admin.activos.sinDato': '—',
  'admin.activos.si': 'Sí',
  'admin.activos.no': 'No',
  'admin.activos.notas': 'Notas',

  'admin.activos.eliminar.titulo': 'Eliminar activo',
  'admin.activos.eliminar.texto':
    'Vas a eliminar {nombre}. Dejará de aparecer al abrir órdenes, y su código y su identificador quedan libres.',
  'admin.activos.eliminar.boton': 'Eliminar activo',
  // Con órdenes no se elimina (#147), como un cliente con historial (#177).
  'admin.activos.eliminar.bloqueado.titulo': 'No se puede eliminar',
  'admin.activos.eliminar.bloqueado.activo':
    '{nombre} tiene {ordenes}. Eliminar es solo para un activo registrado por error. Si ya no se atiende, desactívalo: sus órdenes se conservan y, al abrir una orden nueva, aparece marcado como desactivado.',
  'admin.activos.eliminar.bloqueado.inactivo':
    '{nombre} tiene {ordenes}. Eliminar es solo para un activo registrado por error. Ya está desactivado: sus órdenes se conservan y, al abrir una orden nueva, aparece marcado.',
  'admin.activos.eliminar.bloqueado.ordenes': { one: '1 orden', other: '{n} órdenes' },
  'admin.activos.eliminar.entendido': 'Entendido',
  'admin.activos.eliminar.desactivar': 'Desactivar activo',

  'admin.activos.aviso.creado': 'Se registró el activo',
  'admin.activos.aviso.guardado': 'Se guardaron los cambios del activo',
  'admin.activos.aviso.activado': '{nombre} se vuelve a ofrecer',
  'admin.activos.aviso.desactivado': '{nombre} quedó desactivado',
  'admin.activos.aviso.eliminado': 'Se eliminó {nombre}',
  'admin.activos.error.titulo': 'No se pudo completar',
  // La placa que ya tiene otro activo (#147), en el aviso, con la salida.
  'admin.activos.error.repetido': '{identificador} ya está registrado en el activo {codigo}.',
  'admin.activos.error.repetidoSinCodigo': '{identificador} ya está registrado en otro activo.',
  'admin.activos.error.verExistente': 'Ver activo existente →',
  'admin.activos.error.usarExistente': 'Usar ese activo →',

  // --- Órdenes de servicio (#131) ---
  // El alta rápida desde la placa y la ficha de la orden. «Vehículo» y «placa»
  // fijos: el alta busca por el identificador, que en el taller automotriz es la
  // placa, y «Abrir sin vehículo» cubre al que no trabaja con carros.
  'admin.ordenes.titulo': 'Órdenes de servicio',
  'admin.ordenes.listado.info':
    'Los carros que están en el taller y quién los tiene. Empieza con las órdenes activas de la sucursal elegida arriba; al buscar por folio, placa o cliente salen todas las que coinciden, de cualquier sucursal y estado. Toca una fila para abrir la orden.',
  'admin.ordenes.listado.sinSucursal':
    'El listado muestra las órdenes de la sucursal que está elegida arriba, y no tienes ninguna. Pide que te asignen a una.',
  'admin.ordenes.listado.buscar': 'Buscar por folio, placa o cliente',
  'admin.ordenes.listado.filtro.estado': 'Estado',
  'admin.ordenes.listado.filtro.activas': 'Activas',
  'admin.ordenes.listado.filtro.todas': 'Todas',
  'admin.ordenes.listado.filtro.asignado': 'Asignado',
  'admin.ordenes.listado.filtro.todos': 'Todos',
  'admin.ordenes.listado.filtro.abierta': 'Abierta',
  'admin.ordenes.listado.periodo.cualquiera': 'Cuando sea',
  'admin.ordenes.listado.periodo.hoy': 'Hoy',
  'admin.ordenes.listado.periodo.semana': 'Últimos 7 días',
  'admin.ordenes.listado.periodo.mes': 'Últimos 30 días',
  'admin.ordenes.listado.columna.folio': 'Folio',
  'admin.ordenes.listado.columna.sucursal': 'Sucursal',
  'admin.ordenes.listado.columna.estado': 'Estado',
  'admin.ordenes.listado.columna.entrega': 'Entrega',
  'admin.ordenes.listado.retrabajo': 'Retrabajo',
  'admin.ordenes.listado.vencida': 'Vencida',
  'admin.ordenes.listado.vacio.titulo': 'No hay órdenes en el taller',
  'admin.ordenes.listado.vacio.texto':
    'Aquí aparecen las órdenes de esta sucursal mientras siguen en el taller. Las ya terminadas se ven con Estado: Todas.',
  'admin.ordenes.listado.sinResultados': 'Ninguna orden coincide con «{texto}».',
  'admin.ordenes.volver': 'Volver a Órdenes de servicio',
  'admin.ordenes.nueva.titulo': 'Nueva orden',
  'admin.ordenes.nueva.info':
    'Escribe la placa y elige el vehículo; si no existe, regístralo aquí mismo. Lo demás se puede completar después.',
  'admin.ordenes.nueva.enSucursal': 'En la sucursal {sucursal}',
  'admin.ordenes.nueva.abrir': 'Abrir orden',
  'admin.ordenes.sinSucursal.titulo': 'Elige una sucursal',
  'admin.ordenes.sinSucursal.texto':
    'La orden se abre en la sucursal que está elegida arriba, y no tienes ninguna. Pide que te asignen a una.',
  'admin.ordenes.seccion.vehiculo': 'Vehículo',
  'admin.ordenes.seccion.vehiculoTexto':
    'Busca por la placa, con o sin guiones. Su cliente pasa a ser el de la orden.',
  'admin.ordenes.seccion.cliente': 'Cliente',
  'admin.ordenes.seccion.clienteSinVehiculo': 'Una orden sin vehículo necesita su cliente.',
  'admin.ordenes.seccion.clienteOpcional':
    'Este vehículo no tiene cliente. Puedes ponerlo ahora o después; para entregar la orden hará falta.',
  'admin.ordenes.seccion.trabajo': 'El trabajo',
  'admin.ordenes.sinVehiculo': 'Abrir sin vehículo',
  'admin.ordenes.conVehiculo': 'Buscar por placa',
  'admin.ordenes.cambiarVehiculo': 'Cambiar',
  'admin.ordenes.placa': 'Placa',
  'admin.ordenes.placa.ejemplo': 'P-123ABC',
  'admin.ordenes.encontrados': 'Vehículos con esa placa',
  'admin.ordenes.noExistePlaca': 'No hay ningún vehículo con esa placa.',
  'admin.ordenes.registrar': 'Registrar {placa}',
  'admin.ordenes.desactivado': 'Desactivado',
  // El dueño del carro está inactivo: no se le abre la orden (#177).
  'admin.ordenes.clienteInactivo': 'Cliente inactivo',
  'admin.ordenes.errorBusqueda': 'No se pudo buscar la placa. Vuelve a intentar en un momento.',
  'admin.ordenes.registradoSinElegir':
    'El vehículo se registró, pero no se pudo elegir. Búscalo por su placa.',
  'admin.ordenes.fallaReportada': 'Falla reportada por el cliente',
  'admin.ordenes.fallaReportada.info': 'Lo que el cliente dice que pasa, con sus palabras.',
  'admin.ordenes.asignarA': 'Asignar a',
  'admin.ordenes.sinAsignar': 'Sin asignar',
  'admin.ordenes.entregaPrometida': 'Entrega prometida',
  'admin.ordenes.notas': 'Notas',
  'admin.ordenes.referenciaExterna': 'Referencia externa',
  'admin.ordenes.referenciaExterna.info': 'El número del documento en otro sistema, como la factura del ERP.',
  'admin.ordenes.editar.titulo': 'Editar orden',
  'admin.ordenes.guardarCambios': 'Guardar cambios',
  'admin.ordenes.aviso.abierta': 'Orden abierta',
  'admin.ordenes.aviso.guardada': 'Cambios guardados',
  'admin.ordenes.noExiste.titulo': 'Esta orden no existe',
  'admin.ordenes.noExiste.texto': 'Puede que se haya eliminado, o que la dirección esté mal escrita.',
  'admin.ordenes.ficha.vehiculo': 'Vehículo',
  'admin.ordenes.ficha.cliente': 'Cliente',
  'admin.ordenes.ficha.sinCliente': 'Sin cliente',
  'admin.ordenes.ficha.asignadoA': 'Asignado a',
  'admin.ordenes.ficha.abierta': 'Abierta',
  'admin.ordenes.ficha.por': 'por {persona}',
  'admin.ordenes.ficha.sinFolio': 'Pendiente',
  'admin.ordenes.ficha.editar': 'Editar',
  'admin.ordenes.ficha.retrabajoDe': 'Retrabajo de la orden',
  'admin.ordenes.ficha.info':
    'La orden de trabajo: el vehículo, el cliente y en qué va. Edita la cabecera, cámbiale el estado o márcala con el siguiente paso.',
  'admin.ordenes.mas': 'Más acciones',

  // Cambiar de estado (#135). Lo que falta para entregar sale todo junto en el aviso.
  'admin.ordenes.estado.cambiar': 'Cambiar estado',
  'admin.ordenes.estado.marcarComo': 'Marcar como {estado}',
  'admin.ordenes.estado.cambiado': 'La orden pasó a {estado}',
  'admin.ordenes.estado.noSePudo': 'No se pudo cambiar la orden',
  'admin.ordenes.anular.titulo': 'Pasar a {estado}',
  'admin.ordenes.anular.texto': 'Escribe por qué se anula. Queda en el historial de la orden.',
  'admin.ordenes.anular.motivo': 'Motivo',
  'admin.ordenes.anular.boton': 'Anular orden',
  'admin.ordenes.eliminar.boton': 'Eliminar orden',
  'admin.ordenes.eliminar.titulo': '¿Eliminar la orden {folio}?',
  'admin.ordenes.eliminar.texto':
    'Es para una orden que no debió existir. Se ocultan también sus gastos y sus fotos. Si el trabajo no se va a hacer, lo normal es anularla.',
  'admin.ordenes.aviso.eliminada': 'Orden eliminada',

  // La sección «Trabajo» de la ficha (#133): las líneas de la orden. Sin montos (#137).
  'admin.ordenes.lineas.titulo': 'Trabajo',
  'admin.ordenes.lineas.info':
    'Lo que se le hace al vehículo: servicios, productos y anotaciones. Marca la casilla de una línea cuando esté cumplida; desmárcala si fue un error.',
  'admin.ordenes.lineas.cumplidas': {
    one: '{hechas} de {n} cumplida',
    other: '{hechas} de {n} cumplidas',
  },
  'admin.ordenes.lineas.anotaciones': {
    one: '{n} anotación',
    other: '{n} anotaciones',
  },
  'admin.ordenes.lineas.agregar': 'Servicio o producto',
  'admin.ordenes.lineas.anotar': 'Anotación',
  'admin.ordenes.lineas.vacio': 'Aún no hay trabajo registrado',
  'admin.ordenes.lineas.vacioTexto': 'Agrega los servicios y productos de esta orden, o deja una anotación.',
  'admin.ordenes.lineas.noSePudo': 'No se pudo cambiar la línea',
  'admin.ordenes.lineas.clase.service': 'Servicio',
  'admin.ordenes.lineas.clase.product': 'Producto',
  'admin.ordenes.lineas.clase.note': 'Anotación',
  'admin.ordenes.lineas.dictado': 'Dictado: «{texto}»',
  'admin.ordenes.lineas.cumplidaPor': 'Cumplida por {persona}',
  'admin.ordenes.lineas.entregadaPor': 'Entregada por {persona}',
  'admin.ordenes.lineas.asignadaA': 'Asignada a {persona} · pendiente',
  'admin.ordenes.lineas.pendiente': 'Pendiente',
  'admin.ordenes.lineas.cumplida': 'Cumplida: {linea}',
  'admin.ordenes.lineas.acciones': 'Acciones de {linea}',
  'admin.ordenes.lineas.cantidad': 'Cantidad',
  'admin.ordenes.lineas.cambiarCantidad': 'Cambiar cantidad',
  'admin.ordenes.montos.total': 'Total',
  'admin.ordenes.montos.sinPrecio': 'Sin precio',
  'admin.ordenes.montos.error': 'No se pudieron leer los montos',
  'admin.ordenes.montos.incompleto': 'Total incompleto',
  'admin.ordenes.montos.lineasSinPrecio': {
    one: '{n} línea sin precio',
    other: '{n} líneas sin precio',
  },
  'admin.ordenes.montos.fijado': 'Fijado a mano',
  'admin.ordenes.montos.cambiarPrecio': 'Cambiar precio',
  'admin.ordenes.montos.precioUnitario': 'Precio unitario',
  'admin.ordenes.montos.precioInfo':
    'El precio de una unidad: la línea cobra precio por cantidad. Cambiarlo aquí no toca el precio de lista del catálogo. Déjalo vacío si todavía no tiene precio.',
  'admin.ordenes.montos.cambiarTotal': 'Cambiar total',
  'admin.ordenes.montos.totalInfo':
    'Escribe el total acordado con el cliente —un precio cerrado, un descuento— y dejará de seguir la suma de las líneas. Déjalo vacío para que vuelva a ser la suma.',
  'admin.ordenes.montos.motivo': 'Motivo',
  'admin.ordenes.montos.motivoInfo':
    'Con la orden finalizada, cambiar el total exige decir por qué. Queda en el historial junto al total anterior.',
  'admin.ordenes.lineas.asignar': 'Asignar a otra persona',
  'admin.ordenes.lineas.asignar.titulo': 'Asignar la línea',
  'admin.ordenes.lineas.asignar.info':
    'Para cuando el vehículo se reparte entre varios. Sin nadie elegido, la línea es de quien tenga la orden.',
  'admin.ordenes.lineas.asignar.nadie': 'Quien tenga la orden',
  'admin.ordenes.lineas.ligar': 'Ligar al catálogo',
  'admin.ordenes.lineas.ligar.info':
    'Pasa a ser el servicio o el producto del catálogo y conserva lo que se escribió. Si no tenía precio, toma el de lista.',
  'admin.ordenes.lineas.ligar.campo': 'Servicio o producto',
  'admin.ordenes.lineas.ligar.ejemplo': 'Busca por nombre o código',
  'admin.ordenes.lineas.ligar.boton': 'Ligar',
  'admin.ordenes.lineas.quitar': 'Quitar línea',
  'admin.ordenes.lineas.agregarFoto': 'Agregar foto',
  'admin.ordenes.lineas.quitar.titulo': '¿Quitar esta línea?',
  'admin.ordenes.lineas.quitar.texto': '«{linea}» deja de contar en la orden. Lo que decía se conserva en el historial.',
  'admin.ordenes.lineas.agregar.titulo': 'Agregar servicio o producto',
  'admin.ordenes.lineas.agregar.info':
    'Escribe y elige un servicio o producto del catálogo: toma su precio. Si no está, di qué es y se agrega sin precio; se lo pones después.',
  'admin.ordenes.lineas.agregar.campo': 'Servicio o producto',
  'admin.ordenes.lineas.agregar.ejemplo': 'Cambio de aceite',
  'admin.ordenes.lineas.agregar.boton': 'Agregar',
  'admin.ordenes.lineas.libre.clase': 'Si no está en el catálogo, ¿qué es?',
  'admin.ordenes.lineas.libre.info':
    'Se agrega con lo que escribiste y sin precio. Si después lo das de alta en el catálogo, lígala desde el menú de la línea.',
  'admin.ordenes.lineas.anotar.titulo': 'Agregar anotación',
  'admin.ordenes.lineas.anotar.info':
    'Algo que pasó durante el trabajo y no se cobra ni se cumple: «se le vio la faja gastada».',
  'admin.ordenes.lineas.anotar.campo': 'Anotación',
  'admin.ordenes.lineas.anotar.ejemplo': 'Se le vio la faja gastada',
  'admin.ordenes.lineas.cambiarItem': 'Cambiar',
  'admin.ordenes.lineas.resultados': 'Coincidencias del catálogo',
  'admin.ordenes.lineas.errorBusqueda': 'No se pudo buscar en el catálogo. Vuelve a intentar en un momento.',
  'admin.ordenes.lineas.cerrar': 'Cerrar',
  'admin.ordenes.lineas.aviso.agregada': 'Línea agregada',
  'admin.ordenes.lineas.aviso.ligada': 'Línea ligada al catálogo',
  'admin.ordenes.lineas.aviso.quitada': 'Línea quitada',
  'admin.ordenes.lineas.aviso.fotoAgregada': {
    one: 'Se agregó la foto a «{linea}»',
    other: 'Se agregaron {n} fotos a «{linea}»',
  },
  'admin.ordenes.fotos.titulo': 'Fotos',
  'admin.ordenes.fotos.info':
    'Las fotos de la orden y de sus líneas. «Agregar» sube a la orden en general; la de un trabajo se sube desde el «•••» de su línea, y lleva su nombre debajo.',
  'admin.ordenes.fotos.deLaOrden': 'De la orden',
  'admin.ordenes.historial.info':
    'Lo que le pasó a la orden, lo más nuevo arriba: cuándo se abrió y con qué datos, cada cambio de estado, los cambios de cliente, vehículo, asignado, entrega prometida y falla reportada, el total fijado a mano, las líneas que se agregaron o se quitaron y las fotos que se agregaron o se eliminaron, con quién lo hizo. Las líneas que una persona agrega una tras otra van juntas en un renglón; los cambios de cantidad o de precio de una línea no salen.',
  'admin.ordenes.historial.abierta': 'Se abrió la orden',
  'admin.ordenes.historial.estado': 'Cambió el estado',
  'admin.ordenes.historial.lineaQuitada': 'Línea quitada',
  // Las que una misma persona agregó una tras otra, en un renglón (#198).
  'admin.ordenes.historial.lineasAgregadas': {
    one: 'Línea agregada',
    other: 'Líneas agregadas',
  },
  'admin.ordenes.historial.totalFijado': 'Total fijado a mano',
  'admin.ordenes.historial.totalALaSuma': 'El total vuelve a la suma de las líneas',
  'admin.ordenes.historial.totalDeA': '{a} (antes {de})',
  'admin.ordenes.historial.antes': 'Antes {de}',
  'admin.ordenes.historial.motivo': 'Motivo: {motivo}',
  // Bajo una foto de una línea (#195).
  'admin.ordenes.historial.enLaLinea': 'En la línea «{linea}»',
  // Lo que cambió de la cabecera (#196), cuando el otro lado está vacío.
  'admin.ordenes.historial.sinVehiculo': 'Sin vehículo',
  'admin.ordenes.historial.sinFecha': 'Sin fecha',

  // --- El historial de cualquier registro (#181) ---
  // La línea de tiempo común (`componentes/historial/`). Lo de la orden va arriba,
  // en `admin.ordenes.historial.*`; lo de las maestras, acá: un renglón por guardado.
  'admin.historial.titulo': 'Historial',
  'admin.historial.vacio': 'Todavía no hay nada en el historial.',
  'admin.historial.sinAutor': 'Sin autor',
  'admin.historial.creado': 'Creado',
  'admin.historial.activado': 'Activado',
  'admin.historial.desactivado': 'Desactivado',
  // El pago del gasto (#189), que es su estado.
  'admin.historial.pagado': 'Marcado pagado',
  'admin.historial.pagoQuitado': 'Se quitó la marca de pagado',
  'admin.historial.editado': 'Datos editados',
  'admin.historial.cambio': '{campo}: {de} → {a}',
  // Lo que nació con el registro, bajo «Creado»: no había un antes (#191).
  'admin.historial.valor': '{campo}: {a}',
  // Lo que cambió sin que se sepa a qué: un dato personal, o un monto sin permiso.
  'admin.historial.cambioSinValor': 'Cambió: {campos}',
  'admin.historial.sinValor': '—',
  // Lo nombrado que ya se eliminó: el dueño anterior de un activo (#187).
  'admin.historial.eliminado': 'Eliminado',
  'admin.historial.conDetalle': '{que}: {cual}',
  'admin.historial.contacto.agregado': 'Contacto agregado',
  'admin.historial.contacto.quitado': 'Contacto quitado',
  'admin.historial.contacto.editado': 'Contacto editado',
  'admin.historial.contacto.principal': 'Nuevo contacto principal',
  // Los activos del cliente (#194), por su placa o su código de hoy.
  'admin.historial.activo.agregado': 'Activo agregado',
  'admin.historial.activo.quitado': 'Activo quitado',
  'admin.historial.activo.pasoA': 'Pasó a',
  // Las fotos y los archivos (#195): un PDF no es una foto. El nombre del archivo no viaja.
  'admin.historial.adjunto.fotoAgregada': 'Foto agregada',
  'admin.historial.adjunto.fotoEliminada': 'Foto eliminada',
  'admin.historial.adjunto.archivoAgregado': 'Archivo agregado',
  'admin.historial.adjunto.archivoEliminado': 'Archivo eliminado',

  // --- Página no encontrada ---
  'admin.noEncontrada.titulo': 'Esta página no existe',
  'admin.noEncontrada.texto': 'Revisa la dirección, o vuelve al inicio desde el menú de arriba.',

  // --- Consola de la plataforma (M-8) ---
  //
  // La usa solo el equipo de TallerYa. Los textos de la puerta no dicen qué hay
  // adentro: los lee también quien no debería entrar.
  'plataforma.marca': 'TallerYa · Plataforma',
  'plataforma.nav.inicio': 'Inicio',
  'plataforma.sesion.entrar': 'Entrar',
  'plataforma.sesion.salir': 'Salir',
  'plataforma.acceso.sinSesion.titulo': 'Consola de la plataforma',
  'plataforma.acceso.sinSesion.texto': 'Es solo para el equipo de TallerYa. Entra con tu cuenta.',
  'plataforma.acceso.ajeno.titulo': 'Esta consola no es para tu cuenta',
  'plataforma.acceso.ajeno.texto':
    'Es solo para el equipo de TallerYa. Si trabajas en un taller, tu sitio es el administrativo del taller.',
  'plataforma.acceso.error.titulo': 'No se pudo entrar',
  'plataforma.acceso.error.texto':
    'Keycloak no completó el ingreso. Intenta de nuevo; si sigue igual, revisa que tu cuenta tenga el rol platform-admin.',
  'plataforma.inicio.titulo': 'Consola de la plataforma',
  'plataforma.inicio.entrada': 'Desde aquí se dan de alta los talleres y se ven todos.',
  'plataforma.inicio.aviso.titulo': 'Todavía no hay pantallas',
  'plataforma.inicio.aviso.texto':
    'Esta es la cáscara de la consola: ingreso, navegación y despliegue. El alta de un taller y la lista de talleres llegan en las siguientes tareas.',
  'plataforma.noEncontrada.titulo': 'Esta página no existe',
  'plataforma.noEncontrada.texto': 'Revisa la dirección, o vuelve al inicio desde el menú de arriba.',

  // --- PWA del mecánico (P-1) ---
  //
  // Por ahora solo la pantalla de prueba del armazón. La reemplaza «Mis
  // trabajos de hoy» (P-4).
  'taller.inicio.titulo': 'TallerYa',
  'taller.inicio.entrada':
    'Esta es la cáscara de la aplicación del mecánico. El botón guarda una acción de prueba, que sale al servidor cuando hay señal.',
  'taller.inicio.probar': 'Probar el envío',
  'taller.inicio.guardada': 'Guardada en el celular.',
  'taller.inicio.noGuardada': 'No se pudo guardar en el celular. Vuelve a intentar.',
  'taller.inicio.clave': 'Clave: {clave}',
  // P-9: si el celular protege lo guardado. Es lo primero que hay que ver en un iPhone.
  'taller.inicio.almacenamiento': 'Lo guardado en este celular',
  'taller.inicio.comprobando': 'Comprobando…',
  'taller.inicio.instalada': 'Abierta desde el ícono de inicio.',
  'taller.inicio.enNavegador': 'Abierta en el navegador, no desde el ícono de inicio.',
  'taller.inicio.protegido': 'Protegido: el celular no lo borra solo.',
  'taller.inicio.desprotegido': 'Sin proteger: el celular puede borrarlo si le falta espacio.',
  'taller.inicio.protegidoSinApi': 'Este navegador no deja protegerlo.',
  'taller.inicio.protegidoError': 'El celular dio un error al preguntarle si lo protege ({error}).',
  'taller.inicio.espacio': 'Espacio para guardar: {mb} MB.',

  // La cola sin señal (P-9).
  'taller.cola.pendientes': {
    one: '1 pendiente de sincronizar',
    other: '{n} pendientes de sincronizar',
  },
  'taller.cola.alDia': 'Todo sincronizado',
  'taller.cola.rechazadas': { one: '1 no entró', other: '{n} no entraron' },
  'taller.cola.porDecidir':
    'Esto se intentó enviar hace más de 90 días y no se sabe si llegó. Si lo envías de nuevo, podría quedar repetido.',
  'taller.cola.trabada':
    'Esto no sale: el servidor da un error cada vez. Se sigue intentando solo, y lo de atrás espera. Avisa a quien administra; si lo descartas, no se envía.',
  'taller.cola.enviar': 'Enviar',
  'taller.cola.descartar': 'Descartar',
  'taller.cola.errorDecidir': 'No se pudo hacer en el celular. Vuelve a intentar.',

  // --- Errores que manda la API (B-21) ---
  //
  // La API devuelve CÓDIGOS, nunca texto: `validation.required`. La clave acá es
  // el código con el prefijo `error.`, y `erroresPorCampo()` hace la unión. Los
  // de `validation.*` salen de `ValidationCodes.cs` en el backend: agregar uno
  // allá es agregarlo acá, y `CodigoTraducidoTests` (pruebas de arquitectura)
  // frena el pipeline si falta, con el nombre de la clave.
  'error.desconocido': 'Ocurrió un error inesperado',

  // Los de autenticación salen de `AuthenticationSetup.cs` (B-12). El primero es el
  // que ve alguien cuya sesión venció a mitad de una orden, así que dice qué hacer
  // y no solo qué pasó.
  'error.auth.unauthenticated': 'Tu sesión venció. Vuelve a ingresar.',
  'error.auth.forbidden': 'No tienes permiso para hacer esto',
  'error.auth.no_tenant': 'Tu usuario no tiene un taller asignado',
  'error.auth.tenant_suspended':
    'Este taller está suspendido. Quien lo administra puede comunicarse con TallerYa para reactivarlo',
  'error.auth.device_session_invalid':
    'La sesión de este celular ya no vale. Regístralo de nuevo con un QR',
  'error.auth.qr_no_aceptado':
    'Este código QR no sirve. Si te lo acaban de mostrar, espera unos segundos; si no, pide otro',
  'error.auth.no_membership': 'Tu usuario no pertenece a este taller',
  'error.unexpected': 'Ocurrió un error inesperado',
  'error.request.invalid': 'La petición no se pudo leer',
  'error.request.validation': 'Los datos enviados no son válidos',
  'error.validation.required': 'Este campo es obligatorio',
  'error.validation.too_long': 'Es demasiado largo',
  'error.validation.too_short': 'Es demasiado corto',
  'error.validation.length': 'No tiene el largo esperado',
  'error.validation.too_small': 'Es demasiado pequeño',
  'error.validation.too_large': 'Es demasiado grande',
  'error.validation.out_of_range': 'Está fuera del rango permitido',
  'error.validation.email': 'No es un correo válido',
  'error.validation.format': 'No tiene el formato esperado',
  'error.validation.mismatch': 'No coincide',
  'error.validation.invalid': 'No es válido',

  // Este NO sale de `ValidationCodes.cs`: lo pone un `WithErrorCode` en el
  // validador de personal (M-4), porque no hay regla de FluentValidation que
  // signifique «demasiados elementos en la lista». `CodigoTraducidoTests` también
  // lo vigila: lee cada `WithErrorCode` y cada `Error.*` del backend (#65).
  'error.validation.too_many': 'Son demasiados',

  // Tampoco salen de `ValidationCodes.cs` (#155). `validation.phone` lo pone
  // `ContactFormatRules` en todo teléfono —contactos del cliente, sucursal,
  // personal—, y `document.numero_no_calza`, la API cuando el número no cabe en la
  // máscara de su tipo de documento.
  'error.validation.phone': 'Usa solo números, espacios y + ( ) -',
  'error.document.numero_no_calza': 'El número no tiene la forma de ese documento',

  // Este no lo pone la API: el `CampoNumero` (#156), cuando la coma tecleada no se
  // sabe si es de miles o el decimal —«1,5000»—.
  'error.validation.numero_coma': 'Revisa el número: usa el punto para los decimales',

  // El del slice de referencia (`PingErrors.cs`).
  'error.ping.mensaje_prohibido': 'Ese mensaje no se puede usar para probar el sistema',

  // --- Personal (M-4) ---
  //
  // Salen de `StaffErrors.cs`. Los tres son conflictos de estado, no de forma:
  // el formulario está bien y lo que falla es lo que hay del otro lado.
  'error.staff.no_encontrado': 'No existe esa persona en este taller',
  'error.staff.codigo_tomado':
    'Ese código de empleado ya está en uso. Sigue tomado aunque la persona haya sido eliminada, porque su usuario existe en el sistema de identidad.',
  'error.staff.cuenta_tomada': 'Esa cuenta ya está vinculada a otra persona de este taller',
  'error.staff.no_se_elimina_solo':
    'No puedes eliminarte a ti mismo del taller. Pídeselo a otro administrador.',
  'error.staff.tiene_historial':
    'Esta persona ya tiene trabajo registrado en el taller y no se puede eliminar. Puedes desactivarla',
  'error.staff.correlativo_agotado':
    'No se pudo proponer un código de empleado libre. Vuelve a intentar o escribe uno a mano.',
  'error.staff.sucursal_no_encontrada':
    'Alguna de esas sucursales no existe en este taller, o fue eliminada',

  // El PIN del mecánico (#231), de `StaffPinErrors.cs` y `ChangeOwnPinValidator.cs`.
  'error.staff_pin.ya_tiene_acceso': 'Esta persona ya tiene PIN. Si lo olvidó, restablécelo.',
  'error.staff_pin.sin_acceso': 'Esta persona todavía no tiene acceso. Créaselo primero.',
  'error.staff_pin.persona_inactiva': 'Esta persona está desactivada. Actívala antes de darle acceso.',
  'error.staff_pin.sin_ficha': 'Tu cuenta no tiene ficha de personal en este taller',
  'error.staff_pin.incorrecto': 'El PIN no es correcto',
  'error.staff_pin.igual_al_actual': 'El PIN nuevo tiene que ser distinto del actual',
  'error.staff_pin.en_espera':
    'Pusiste mal el PIN demasiadas veces. Espera 15 minutos y vuelve a intentar',
  'error.staff_pin.solo_otro_dueno':
    'Al dueño del taller solo otro dueño le puede dar o restablecer el PIN',
  'error.device.qr_no_valido': 'Este código QR ya no sirve. Pídele otro al administrador',
  'error.device.qr_solo_empleados':
    'El QR es para que un empleado registre su celular. Quien administra el taller entra al sitio administrativo con su cuenta',
  'error.device.no_valido': 'Este celular ya no está registrado en el taller. Regístralo de nuevo',
  'error.device.no_encontrado': 'No existe ese celular en este taller',
  'error.staff_pin.formato': 'El PIN son 4 dígitos',

  // --- Identidad ---
  //
  // Salen de `IdentityErrors.cs`: la cuenta de Keycloak que se crea o se vincula
  // al dar de alta a alguien del personal.
  'error.identity.user.username_taken_by_other_tenant':
    'Ese nombre de usuario ya lo usa otro taller',
  'error.identity.membership.unknown_role': 'El rol pedido no existe',
  'error.identity.membership.revoked':
    'Esa persona fue dada de baja de este taller. Devolverle el acceso lo decide un administrador.',
  'error.identity.staff.already_linked': 'Esa persona ya tiene otra cuenta vinculada',
  'error.identity.staff.account_taken':
    'Esa cuenta ya está vinculada a otra persona de este taller',
  'error.identity.staff.account_missing':
    'La cuenta vinculada a esa persona ya no existe',
  'error.identity.staff.account_other_tenant':
    'La cuenta vinculada a esa persona es de otro taller',
  'error.identity.user.username_taken':
    'El nombre de usuario que le corresponde a esa persona ya lo tiene otra cuenta',
  'error.identity.tenant.not_found': 'El taller no existe',
  'error.identity.user.email_required':
    'Esa persona no tiene correo, y lo necesita para entrar al sistema',
  'error.identity.user.email_taken_by_other_account':
    'Ese correo ya lo tiene la cuenta de otra persona del taller',
  'error.identity.enrollment.personal_account':
    'Esta persona entra con su correo: el QR de registro es solo para cuentas de mecánico',

  // --- Alta de talleres ---
  //
  // Salen de `PlatformErrors.cs`. Los ve la consola de la plataforma, no un
  // taller.
  'error.tenant.slug_tomado': 'Ya hay un taller con ese slug',
  'error.tenant.codigo_tomado': 'Ya hay un taller con ese código',
  'error.tenant.id_ocupado':
    'Ese id ya es de otro taller. Un alta que se reintenta tiene que traer los mismos datos.',

  // --- Configuración (B-24) ---
  //
  // Salen de `SettingErrors.cs`. Se agregan ahora, con el backend, aunque la
  // pantalla de ajustes no exista todavía: es lo que evita que el día que exista
  // muestre «Ocurrió un error inesperado» sin que nadie sepa por qué.
  'error.settings.no_existe': 'Ese ajuste no existe',
  'error.settings.nivel_no_permitido':
    'Ese ajuste no se puede fijar a ese nivel: solo a uno más general.',
  'error.settings.valor_invalido':
    'Ese valor no corresponde al tipo del ajuste, o no está entre sus opciones',
  'error.settings.no_editable': 'Ese ajuste no lo cambia el taller. Pídelo por soporte.',
  'error.settings.escritura_simultanea':
    'Otra persona cambió ese ajuste al mismo tiempo. Vuelve a guardar.',

  // Salen de `RolePermissionErrors.cs` (#47). Como los de ajustes, llegan con el
  // backend, antes que la pantalla de permisos (#240).
  'error.role_permission.locked':
    'Ese permiso no se le puede quitar al dueño: sin él, nadie en el taller podría devolvérselo.',
  'error.role_permission.role_not_found': 'Ese rol no existe',
  'error.role_permission.permission_not_found': 'Ese permiso no existe',

  // --- Choques de llave única (B-29) ---
  //
  // Los manda el paso 5 de la tubería cuando dos peticiones simultáneas escriben
  // lo mismo y la base arbitra. Salen de `UniqueViolations.cs`, y
  // `CodigoTraducidoTests` frena el pipeline si falta alguno.
  //
  // Ninguno pide corregir un campo: no hay nada mal escrito, hay otra persona
  // guardando al mismo tiempo. Por eso los dos primeros dicen qué hacer.
  'error.specialty.duplicada':
    'Otra persona guardó una especialidad con ese mismo nombre al mismo tiempo. Vuelve a intentar.',
  'error.specialty.no_encontrada': 'No existe esa especialidad en este taller',
  'error.specialty.nombre_duplicado': 'Ya existe una especialidad con ese nombre en este taller',
  'error.specialty.en_uso':
    'Esa especialidad la tiene alguna ficha, aunque sea de alguien eliminado: desactívala en vez de eliminarla.',

  // Las de sucursales (M-1D). El nombre se compara sin tildes, sin mayúsculas y
  // sin espacios de sobra —así lo compara la llave única—, así que el texto no
  // dice «ese nombre exacto».
  'error.location.codigo_tomado': 'Ese código de sucursal ya está en uso en este taller',
  'error.location.nombre_duplicado': 'Ya existe una sucursal con ese nombre en este taller',
  'error.location.principal_simultanea':
    'Otra persona cambió la sucursal principal al mismo tiempo. Vuelve a intentar.',
  // Las de los casos de uso de sucursales (M-1). Dicen qué hacer antes, no solo
  // que no se puede: es lo que ve quien apretó el botón.
  'error.location.no_encontrada': 'No existe esa sucursal en este taller',
  'error.location.correlativo_agotado':
    'No se pudo proponer un código de sucursal libre. Vuelve a intentar o escribe uno a mano.',
  'error.location.principal_no_se_desactiva':
    'La sucursal principal no se saca de servicio. Nombra primero otra como principal.',
  'error.location.principal_no_se_elimina':
    'La sucursal principal no se elimina. Nombra primero otra como principal.',
  'error.location.ultima_no_se_elimina': 'No se puede eliminar la única sucursal del taller',
  'error.location.inactiva_no_es_principal':
    'Una sucursal fuera de servicio no puede ser la principal. Ponla en servicio primero.',

  // Las del catálogo (M-3D). La del nombre aclara «de la misma clase» porque un
  // servicio y un producto se pueden llamar igual: el `kind` está en la llave.
  'error.catalog_item.codigo_tomado': 'Ese código ya está en uso en el catálogo de este taller',
  'error.catalog_item.no_encontrado': 'No existe ese producto o servicio en este taller',
  'error.catalog_item.en_ordenes': 'Este producto o servicio aparece en órdenes y no se puede eliminar. Puedes desactivarlo',
  'error.catalog_item.correlativo_agotado':
    'No se pudo proponer un código libre. Vuelve a intentar o escribe uno a mano',
  'error.catalog_item.categoria_no_disponible': 'Esa categoría no existe o ya no está activa',
  'error.catalog_item.duracion_solo_servicios': 'La duración de referencia es solo para servicios',
  'error.catalog_item.nombre_duplicado':
    'Ya existe otro ítem con ese nombre y de la misma clase —servicio o producto— en este taller',
  'error.catalog_import.formato_no_soportado': 'El archivo tiene que ser Excel (.xlsx) o CSV',
  'error.catalog_import.archivo_ilegible':
    'No se pudo leer el archivo. Revisa que sea un Excel o un CSV y vuelve a intentar',
  'error.catalog_import.archivo_vacio': 'El archivo no trae filas para importar',
  'error.catalog_import.demasiadas_filas':
    'El archivo trae demasiadas filas. Pártelo en varios y vuelve a intentar',
  'error.catalog_import.columnas_faltantes':
    'Faltan columnas: el archivo tiene que traer al menos «Tipo» y «Nombre». Usa la plantilla',
  'error.catalog_import.tipo_requerido': 'Falta el tipo: servicio o producto',
  'error.catalog_import.tipo_invalido': 'El tipo tiene que ser servicio o producto',
  'error.catalog_import.nombre_requerido': 'Falta el nombre',
  'error.catalog_import.nombre_largo': 'El nombre es demasiado largo',
  'error.catalog_import.codigo_largo': 'El código es demasiado largo',
  'error.catalog_import.categoria_larga': 'El nombre de la categoría es demasiado largo',
  'error.catalog_import.precio_invalido': 'El precio no es un número válido',
  'error.catalog_import.precio_decimales': 'El precio admite hasta seis decimales',
  'error.catalog_import.duracion_invalida':
    'La duración tiene que ser un número entero de minutos, mayor que cero',
  'error.catalog_import.codigo_repetido': 'Ese código ya viene en otra fila del archivo',
  'error.catalog_import.nombre_repetido': 'Ese nombre ya viene en otra fila del archivo, para el mismo tipo',
  'error.catalog_import.categoria_inactiva': 'Esa categoría está desactivada en este taller',
  'error.catalog_category.nombre_duplicado':
    'Ya existe una categoría con ese nombre en este taller',
  'error.catalog_category.no_encontrada': 'No existe esa categoría en este taller',
  'error.catalog_category.en_uso':
    'Esa categoría la tiene algún producto o servicio, aunque esté eliminado: desactívala en vez de eliminarla.',

  // Las de marcas y modelos (M-7D). La de la marca aclara «para ese tipo de
  // activo» porque el tipo está en la llave: una marca genérica y una acotada a
  // un tipo conviven.
  'error.asset_brand.nombre_duplicado':
    'Ya existe una marca con ese nombre para ese tipo de activo en este taller',
  'error.asset_model.nombre_duplicado':
    'Esa marca ya tiene un modelo con ese nombre en este taller',
  'error.asset_brand.no_encontrada': 'No existe esa marca en este taller',
  'error.asset_model.no_encontrado': 'No existe ese modelo en este taller',
  'error.asset_type.no_encontrado': 'No existe ese tipo de activo en este taller',
  'error.asset_type.apagado': 'Ese tipo de activo está desactivado: no se ofrece para activos nuevos',
  'error.asset.no_encontrado': 'No existe ese activo en este taller',
  'error.asset.cliente_no_encontrado': 'No existe ese cliente en este taller',
  'error.asset.cliente_inactivo':
    '{customerName} está inactivo: no se le pueden asignar activos. Actívalo antes de continuar.',
  'error.asset.marca_de_otro_tipo': 'Esa marca es de otro tipo de activo',
  'error.asset.modelo_de_otra_marca': 'Ese modelo no es de la marca elegida',
  'error.asset_brand.apagada': 'Esa marca está desactivada: elige otra',
  'error.asset_model.apagado': 'Ese modelo está desactivado: elige otro',
  'error.asset.identificador_requerido': 'Este tipo de activo lo exige',
  'error.asset.tiene_historial': 'Este activo tiene órdenes registradas y no se puede eliminar. Puedes desactivarlo',
  'error.asset.correlativo_agotado':
    'No se pudo proponer un código libre. Vuelve a intentar o escribe uno a mano',
  'error.asset.detalle.no_es_opcion': 'Elige una de las opciones de la lista',
  'error.asset.detalle.desconocido': 'Este tipo de activo no tiene ese dato',
  'error.asset.detalle.apagado': 'Ese dato ya no se pide para este tipo de activo',
  'error.asset_type.nombre_duplicado': 'Ya existe un tipo de activo con ese nombre en este taller',
  'error.asset_type_field.no_encontrado': 'Ese tipo de activo no tiene ese campo',
  'error.asset_type_field.lista_sin_opciones': 'Una lista necesita al menos una opción',
  'error.asset_type_field.opciones_repetidas': 'Hay dos opciones con el mismo nombre',

  // Las de activos y sus tipos (M-6D). El identificador se compara dentro de su
  // tipo y sin guiones ni espacios.
  'error.asset.codigo_tomado': 'Ese código ya lo tiene otro activo de este taller',
  'error.asset.identificador_tomado':
    'Ya hay otro activo de ese tipo con ese identificador en este taller',
  'error.asset_type.codigo_tomado': 'Ya existe un tipo de activo con ese código en este taller',
  'error.asset_type_field.codigo_repetido': 'Ese tipo de activo ya tiene un campo con ese código',

  // Las de clientes (M-5D). El documento y los contactos se comparan
  // normalizados: sin guiones ni espacios, y el correo sin mayúsculas.
  'error.customer.codigo_tomado': 'Ese código ya lo tiene otro cliente de este taller',
  'error.customer.documento_tomado': 'Ya hay otro cliente con ese documento en este taller',
  'error.customer.telefono_repetido': 'Este cliente ya tiene ese teléfono',
  'error.customer.correo_repetido': 'Este cliente ya tiene ese correo',
  'error.customer.principal_simultaneo':
    'Otra persona cambió el contacto principal al mismo tiempo. Vuelve a intentar.',

  // Las de los casos de uso de clientes (M-5B). `tiene_historial` viaja con
  // `orderCount`, `assetCount` e `isActive`, que arma la ventana del rechazo (#177).
  'error.customer.no_encontrado': 'No existe ese cliente en este taller',
  'error.customer.correlativo_agotado':
    'No se pudo proponer un código de cliente libre. Vuelve a intentar o escribe uno a mano.',
  'error.customer.documento_no_aplica': 'Ese tipo de documento no aplica a este cliente',
  'error.customer.nombre_comercial_solo_empresa': 'Solo una empresa tiene nombre comercial',
  'error.customer.tiene_historial':
    'Este cliente tiene órdenes o activos registrados y no se puede eliminar. Puedes desactivarlo.',

  // La exportación de clientes (#107).
  'error.customer_export.demasiadas_filas':
    'Hay más de 10000 clientes con estos filtros. Acótalos y vuelve a intentar.',

  // Las de la importación de clientes (#106). Las del archivo entero salen como
  // 400; las de una fila viajan en la respuesta, junto a las del alta.
  'error.customer_import.formato_no_soportado': 'El archivo tiene que ser Excel (.xlsx) o CSV',
  'error.customer_import.archivo_muy_grande': 'El archivo pasa de 5 MB',
  'error.customer_import.archivo_ilegible':
    'No se pudo leer el archivo. Revisa que sea la plantilla guardada como Excel o CSV.',
  'error.customer_import.demasiadas_filas':
    'El archivo trae más de 5000 clientes. Pártelo en varios archivos.',
  'error.customer_import.columnas_faltantes':
    'Al archivo le faltan las columnas tipo y nombre. Descarga la plantilla y copia tus datos en ella.',
  'error.customer_import.sin_filas': 'El archivo no trae ningún cliente',
  'error.customer_import.tipo_invalido': 'Tiene que ser Persona o Empresa',
  'error.customer_import.tipo_documento_desconocido':
    'Ese tipo de documento no está entre los que usa el taller',
  'error.customer_import.codigo_repetido_en_archivo': 'Otra fila del archivo ya trae ese código',
  'error.customer_import.documento_repetido_en_archivo':
    'Otra fila del archivo ya trae ese documento',
  'error.customer_import.lote_deshecho':
    'No se guardó: otra persona dio de alta un cliente igual mientras se importaba',

  // Las de proveedores (M-2D). El nombre y el documento se comparan normalizados.
  'error.supplier.codigo_tomado': 'Ese código ya lo tiene otro proveedor de este taller',
  'error.supplier.nombre_duplicado': 'Ya existe un proveedor con ese nombre en este taller',
  'error.supplier.documento_tomado': 'Ya hay otro proveedor con ese documento en este taller',
  'error.supplier.nombre_y_documento_duplicados':
    'Ya existe un proveedor con ese nombre y otro con ese documento en este taller',
  'error.supplier.no_encontrado': 'No existe ese proveedor en este taller',
  'error.supplier.documento_no_aplica': 'Ese tipo de documento no está disponible en este taller',
  'error.supplier.correlativo_agotado':
    'No se pudo proponer un código de proveedor libre. Vuelve a intentar o escribe uno a mano.',

  // Las de adjuntos (B-26). El tipo y el tamaño dicen qué se acepta, porque
  // «archivo inválido» no le dice a nadie qué hacer.
  'error.attachment.no_encontrado': 'No existe ese adjunto en este taller',
  'error.attachment.activo_no_encontrado': 'No existe ese activo en este taller',
  'error.attachment.orden_no_encontrada': 'No existe esa orden en este taller',
  'error.attachment.linea_no_encontrada': 'No existe esa línea en esa orden',
  'error.attachment.gasto_no_encontrado': 'No existe ese gasto en este taller',
  'error.attachment.tipo_no_permitido': 'Solo se aceptan fotos (JPEG, PNG, WebP, HEIC) y PDF',
  'error.attachment.demasiado_grande': 'El archivo pasa de 20 MB',
  'error.attachment.contenido_no_coincide': 'El archivo no es del tipo que dice ser',
  'error.attachment.id_en_uso': 'Ese adjunto ya se subió para otra cosa',
  'error.attachment.fecha_fuera_de_rango':
    'La fecha de la foto no es válida: revisa la hora del dispositivo',

  // Las de estados de la orden (O-1D). El nombre se compara normalizado, y uno
  // inactivo sigue ocupando el suyo.
  'error.order_status.nombre_duplicado': 'Ya existe un estado de orden con ese nombre en este taller',
  'error.order_status.inicial_simultaneo':
    'Otra persona cambió el estado inicial al mismo tiempo. Vuelve a intentar.',

  // Configuraciones › Estados de la orden (O-13). Dicen «etapa», como la pantalla.
  'error.order_status.no_encontrado': 'No existe ese estado de orden en este taller',
  'error.order_status.inicial_no_se_desactiva':
    'El estado inicial no se desactiva: con él nace toda orden. Nombra primero otro como inicial.',
  'error.order_status.ultimo_de_su_etapa':
    'Es el último estado activo de su etapa: sin él, ninguna orden podría pasar por ella.',
  'error.order_status.inicial_solo_abierto':
    'El estado inicial es de la etapa Abierta. Nombra primero otro como inicial y luego cámbiale la etapa.',
  'error.order_status.solo_abierto_es_inicial': 'Solo un estado de la etapa Abierta puede ser el inicial',
  'error.order_status.inactivo_no_es_inicial': 'Un estado desactivado no puede ser el inicial. Actívalo primero.',
  'error.order_status.orden_desactualizado':
    'La lista de estados cambió mientras la ordenabas. Vuelve a intentar.',
  'error.order_status.con_ordenes':
    'Este estado tiene órdenes: su etapa y si congela los montos no se cambian. Mueve primero sus órdenes a otro estado, o crea uno nuevo en la etapa que quieres.',

  // Las de órdenes (O-2D). El folio es único en todo el taller, no por sucursal.
  'error.service_order.folio_tomado': 'Ese folio ya lo tiene otra orden de este taller',

  // Abrir, leer y editar la orden (O-4B). Lo que la orden rechaza llega todo junto:
  // el ProblemDetails trae en `problems` los códigos de cada cosa que falta.
  'error.service_order.no_encontrada': 'No existe esa orden en este taller',
  'error.service_order.folio_agotado':
    'No se pudo dar un folio libre a la orden. Revisa el formato del folio en Configuraciones.',
  'error.service_order.sucursal_no_encontrada': 'No existe esa sucursal en este taller',
  'error.service_order.sucursal_apagada': 'Esa sucursal está desactivada: no abre órdenes nuevas',
  'error.service_order.cliente_no_encontrado': 'No existe ese cliente en este taller',
  // `customerName` viene en el 409 (#177): el cliente puede no haberse elegido a
  // mano, si la orden se abrió por la placa.
  'error.service_order.cliente_inactivo':
    '{customerName} está inactivo: no se le pueden asignar órdenes. Actívalo antes de continuar.',
  'error.service_order.activo_no_encontrado': 'No existe ese activo en este taller',
  'error.service_order.personal_no_encontrado': 'No existe esa persona en el personal de este taller',
  'error.service_order.personal_apagado': 'Esa persona está desactivada: no se le asignan órdenes',
  'error.service_order.orden_anterior_no_encontrada': 'No existe la orden anterior en este taller',
  'error.service_order.sin_cliente_ni_activo': 'Una orden necesita un cliente o un activo',
  'error.service_order.falta_cliente': 'Para entregar la orden hace falta el cliente',
  'error.service_order.falta_folio': 'Para entregar la orden hace falta el folio, que llega al sincronizar',
  'error.service_order.falta_activo': 'Para entregar la orden hace falta el activo: el taller lo exige',
  'error.service_order.falta_motivo_de_anulacion': 'Para anular la orden hace falta el motivo',
  'error.service_order.estado_apagado': 'Ese estado está desactivado',
  'error.service_order.montos_congelados':
    'La orden está finalizada y sus montos no cambian. Regrésala a un estado no final para editarlos.',
  'error.service_order.total_sin_motivo': 'Cambiar el total de una orden finalizada exige decir por qué',
  'error.service_order.anulada': 'Una orden anulada no tiene total que cambiar',
  'error.service_order.finalizada': 'Una orden finalizada no se elimina. Regrésala primero a un estado no final.',
  'error.service_order.anulada_no_se_entrega':
    'Una orden anulada no se entrega directamente. Reábrela primero en un estado de trabajo.',
  'error.service_order.estado_no_encontrado': 'No existe ese estado de orden en este taller',
  'error.service_order.linea_no_encontrada': 'No existe esa línea en la orden',
  'error.service_order.linea_repetida': 'Ya existe una línea con ese identificador',
  'error.service_order.monto_fuera_de_rango':
    'El monto es más grande de lo que el sistema puede guardar. Revisa la cantidad y el precio.',
  'error.service_order.item_no_encontrado': 'No existe ese servicio o producto en el catálogo de este taller',
  'error.service_order.item_apagado': 'Ese servicio o producto está desactivado en el catálogo',
  'error.service_order.linea_ya_ligada': 'Esa línea ya es un servicio o un producto del catálogo',
  'error.service_order.linea_es_anotacion': 'Una anotación no tiene cantidad ni precio, y no se marca como cumplida',
  'error.service_order.sin_ficha_de_personal':
    'Tu usuario no tiene ficha de personal en este taller: pide que la vinculen para marcar trabajo cumplido',

  'error.expense.factura_repetida': 'Esa factura ya está registrada como gasto en este taller',
  'error.expense.no_encontrado': 'No existe ese gasto en este taller',
  'error.expense.sucursal_no_encontrada': 'No existe esa sucursal en este taller',
  'error.expense.sucursal_apagada': 'Esa sucursal está desactivada: no registra gastos nuevos',
  'error.expense.orden_no_encontrada': 'No existe esa orden en este taller',
  'error.expense.proveedor_no_encontrado': 'No existe ese proveedor en este taller',
  'error.expense.proveedor_apagado': 'Ese proveedor está desactivado: no se le cargan gastos nuevos',
  'error.expense.pago_antes_de_compra': 'La fecha de pago no puede ser anterior a la de compra',
  'error.expense.fecha_fuera_de_rango': 'Revisa la fecha: no parece una fecha de compra o de pago',

  // El genérico, para la restricción que todavía nadie mapeó. No promete que
  // reintentar sirva, porque sin saber qué chocó no se puede prometer.
  'error.conflict.duplicado': 'Ya existe un registro con esos datos',

  // Una edición que se quedó vieja (#77): otra persona guardó mientras esta tenía
  // el formulario abierto. No es un campo mal llenado, así que va en el aviso
  // general y dice qué hacer. Que lo escrito no se guardó va dicho (#178): al
  // recargar se pierde, y hay que volver a hacerlo sobre la versión nueva.
  'error.conflict.cambio_simultaneo':
    'Alguien más guardó cambios en este registro mientras lo editabas. Recarga para ver la versión actual y vuelve a hacer tus cambios: lo que escribiste aquí no se guardó.',

  // La idempotencia (B-32). Los tres los provoca la app que manda la clave, no la
  // persona: si uno llega a la pantalla, es un bug del cliente o un reintento que
  // se cruzó con otro. Por eso dicen qué hacer y no culpan a nadie.
  'error.idempotency.clave_invalida': 'El pedido llegó con una clave de reintento inválida',
  'error.idempotency.clave_reusada': 'Ese pedido no coincide con el que se envió antes con la misma clave',
  'error.idempotency.en_curso': 'Ese pedido ya se está procesando. Vuelve a intentar en un momento.',
} as const;
